<script setup lang="ts">
import { reactive } from 'vue';

defineEmits<{
  (event: 'click', section: string): void;
}>();

const sectionNames = [
  'access',
  'frontage',
  'stormwater',
  'tree',
  'utility',
] as const;
type SectionName = typeof sectionNames[number];

type SectionLinks = Record<SectionName, string>;

defineProps({
  links: {
    type: Object as () => SectionLinks,
    required: true,
  },
});

const focusStyles = {
  outline: 'none',
  filter: 'drop-shadow(10px 10px gray)',
};

const focus = reactive<Record<SectionName, boolean>>({
  access: false,
  frontage: false,
  stormwater: false,
  tree: false,
  utility: false,
});

const handleFocus = (sectionName: SectionName) => {
  for (const name of sectionNames) {
    focus[name] = false;
  }
  focus[sectionName] = true;
};
</script>

<template>
  <svg
    version="1.1"
    xmlns="http://www.w3.org/2000/svg"
    xmlns:xlink="http://www.w3.org/1999/xlink"
    viewBox="350 100 1250 850"
    alt="Rendering of a house and elements of the public right-of-way surrounding it"
  >
    <g id="Layer_2">
      <g>
        <line class="st0" x1="1134.8" y1="539.8" x2="1275.9" y2="621.7" />
        <circle class="st1" cx="1275.9" cy="621.7" r="6.3" />
        <circle class="st2" cx="1135.9" cy="540.3" r="6.3" />
        <circle class="st3" cx="1275.9" cy="621.7" r="4.7" />
      </g>
      <line class="st4" x1="757.9" y1="604.5" x2="757.9" y2="685.3" />
      <g>
        <line class="st5" x1="599.2" y1="584" x2="874.3" y2="743.7" />
        <circle class="st1" cx="869.8" cy="741.2" r="9.7" />
        <circle class="fill-orange-500" cx="602.5" cy="585.9" r="9.1" />
        <circle class="st3" cx="869.8" cy="741.2" r="7.3" />
      </g>
      <g>
        <g>
          <g>
            <polygon
              class="st7"
              points="979.9,290.3 879.1,348.5 979.9,406.7 1080.6,348.5     "
            />
          </g>
        </g>
        <g>
          <g>
            <polygon
              class="st7"
              points="1080.6,348.5 979.9,406.7 1080.7,464.9 1181.4,406.7     "
            />
          </g>
        </g>
        <g>
          <g>
            <polygon
              class="st7"
              points="879.2,348.5 778.4,406.7 879.2,464.9 979.9,406.7     "
            />
          </g>
        </g>
        <g>
          <g>
            <polygon
              class="st7"
              points="980,406.7 879.2,464.9 980,523 1080.7,464.9     "
            />
          </g>
        </g>
        <g>
          <polygon
            class="st7"
            points="1181.4,406.7 1080.6,464.9 1181.4,523.1 1282.1,464.9    "
          />
        </g>
        <g>
          <g>
            <polygon
              class="st7"
              points="1080.8,464.8 980,523 1080.8,581.1 1181.5,523     "
            />
          </g>
        </g>
        <g>
          <g>
            <polygon
              class="st7"
              points="778.5,406.7 677.7,464.9 778.5,523 879.2,464.9     "
            />
          </g>
        </g>
        <g>
          <g>
            <polygon
              class="st7"
              points="879.3,464.8 778.5,523 879.3,581.2 980,523     "
            />
          </g>
        </g>
        <g>
          <g>
            <polygon
              class="st7"
              points="980.1,522.9 879.3,581.1 980.1,639.3 1080.8,581.1     "
            />
          </g>
        </g>
        <g>
          <g>
            <polygon
              class="st7"
              points="1284.7,463.5 1181.4,523.2 1282.2,581.3 1388,524     "
            />
          </g>
        </g>
        <g>
          <g>
            <polygon
              class="st7"
              points="1181.5,523.1 1080.7,581.3 1181.5,639.5 1282.2,581.3     "
            />
          </g>
        </g>
        <g>
          <g>
            <polygon
              class="st7"
              points="1080.8,581.3 980,639.5 1080.8,697.6 1181.5,639.5     "
            />
          </g>
        </g>
        <g>
          <g>
            <polygon
              class="st7"
              points="677.9,464.7 577.1,522.9 677.9,581.1 778.7,522.9     "
            />
          </g>
        </g>
        <g>
          <g>
            <polygon
              class="st7"
              points="778.7,522.9 677.9,581.1 778.7,639.3 879.5,581.1     "
            />
          </g>
        </g>
        <g>
          <g>
            <polygon
              class="st7"
              points="879.6,581 778.7,639.2 879.6,697.4 980.3,639.2     "
            />
          </g>
        </g>
        <g>
          <g>
            <polygon
              class="st7"
              points="980.3,639.3 879.5,697.5 980.3,755.7 1081,697.5     "
            />
          </g>
        </g>
      </g>
      <polygon class="st7" points="1388,507.7 1388,524 1373.9,515.8  " />
      <polygon class="st7" points="591.3,514.7 577.3,523 577.1,506.7  " />
      <g>
        <polygon
          class="st8"
          points="1286.6,449.6 1185.3,507.7 1185.3,507.7 1083.9,566 1083.9,566 982.9,623.9 982.9,623.9 881.5,682.2     982.9,740.3 1084,682.3 1084,682.3 1185.3,624.1 1286.7,566 1286.7,566 1388,507.8   "
        />
        <polygon
          class="st8"
          points="982.7,623.9 982.7,624 1084,565.8 1185.2,507.7 1185.3,507.7 1286.6,449.6 1185.3,391.4 982.5,275     577.3,507.5 678.7,565.7 678.7,565.7 780.1,623.9 780.2,623.9 881.6,682   "
        />
      </g>
      <g>
        <g>
          <g>
            <polygon
              class="st7"
              points="982.7,274.2 898.7,322.7 982.7,371.2 1066.6,322.7     "
            />
          </g>
        </g>
        <g>
          <g>
            <polygon
              class="st7"
              points="1066.6,322.7 982.7,371.2 1066.7,419.6 1150.6,371.2     "
            />
          </g>
        </g>
        <g>
          <g>
            <polygon
              class="st7"
              points="898.8,322.6 814.8,371.1 898.8,419.6 982.7,371.1     "
            />
          </g>
        </g>
        <g>
          <g>
            <polygon
              class="st7"
              points="982.8,371.1 898.8,419.6 982.8,468.1 1066.7,419.6     "
            />
          </g>
        </g>
        <g>
          <polygon
            class="st7"
            points="1150.6,371.2 1066.6,419.7 1150.6,468.1 1234.6,419.7    "
          />
        </g>
        <g>
          <g>
            <polygon
              class="st7"
              points="1066.8,419.5 982.8,468 1066.8,516.5 1150.7,468     "
            />
          </g>
        </g>
        <g>
          <g>
            <polygon
              class="st7"
              points="814.8,371.1 730.8,419.6 814.8,468.1 898.8,419.6     "
            />
          </g>
        </g>
        <g>
          <g>
            <polygon
              class="st7"
              points="898.8,419.6 814.8,468.1 898.8,516.6 982.8,468.1     "
            />
          </g>
        </g>
        <g>
          <g>
            <polygon
              class="st7"
              points="982.8,468 898.8,516.5 982.8,565 1066.8,516.5     "
            />
          </g>
        </g>
        <g>
          <g>
            <polygon
              class="st7"
              points="1234.6,419.7 1150.6,468.2 1234.6,516.7 1318.5,468.2     "
            />
          </g>
        </g>
        <g>
          <g>
            <polygon
              class="st7"
              points="1150.7,468.1 1066.7,516.7 1150.7,565.1 1234.6,516.6     "
            />
          </g>
        </g>
        <g>
          <g>
            <polygon
              class="st7"
              points="1066.8,516.6 982.8,565.1 1066.8,613.6 1150.7,565.1     "
            />
          </g>
        </g>
        <g>
          <g>
            <polygon
              class="st7"
              points="731.1,419.5 647.1,468 731.1,516.5 815,468     "
            />
          </g>
        </g>
        <g>
          <g>
            <polygon
              class="st7"
              points="815.1,468 731.1,516.5 815.1,564.9 899,516.5     "
            />
          </g>
        </g>
        <g>
          <g>
            <polygon
              class="st7"
              points="899.1,516.4 815.1,564.9 899.1,613.4 983,564.9     "
            />
          </g>
        </g>
        <g>
          <g>
            <polygon
              class="st7"
              points="983,565 899,613.5 983,662 1067,613.5     "
            />
          </g>
        </g>
      </g>
      <g>
        <g>
          <g>
            <g>
              <polygon
                class="st2"
                points="982.7,260.6 898.7,309.1 982.7,357.6 1066.6,309.1      "
              />
            </g>
          </g>
          <g>
            <g>
              <polygon
                class="st2"
                points="1066.6,309.1 982.7,357.6 1066.7,406.1 1150.6,357.6      "
              />
            </g>
          </g>
          <g>
            <g>
              <polygon
                class="st2"
                points="898.8,309.1 814.8,357.6 898.8,406.1 982.7,357.6      "
              />
            </g>
          </g>
          <g>
            <g>
              <polygon
                class="st2"
                points="982.8,357.6 898.8,406.1 982.8,454.5 1066.7,406.1      "
              />
            </g>
          </g>
          <g>
            <polygon
              class="st2"
              points="1150.6,357.6 1066.6,406.1 1150.6,454.6 1234.6,406.1     "
            />
          </g>
          <g>
            <g>
              <polygon
                class="st2"
                points="1066.8,406 982.8,454.5 1066.8,503 1150.7,454.5      "
              />
            </g>
          </g>
          <g>
            <g>
              <polygon
                class="st2"
                points="814.8,357.6 730.8,406 814.8,454.5 898.8,406.1      "
              />
            </g>
          </g>
          <g>
            <g>
              <polygon
                class="st2"
                points="898.8,406 814.8,454.5 898.8,503 982.8,454.5      "
              />
            </g>
          </g>
          <g>
            <g>
              <polygon
                class="st2"
                points="982.8,454.4 898.8,503 982.8,551.4 1066.8,502.9      "
              />
            </g>
          </g>
          <g>
            <g>
              <polygon
                class="st2"
                points="1234.6,406.1 1150.6,454.6 1234.6,503.1 1318.5,454.6      "
              />
            </g>
          </g>
          <g>
            <g>
              <polygon
                class="st2"
                points="1150.7,454.6 1066.7,503.1 1150.7,551.6 1234.6,503.1      "
              />
            </g>
          </g>
          <g>
            <g>
              <polygon
                class="st2"
                points="1066.8,503.1 982.8,551.6 1066.8,600 1150.7,551.5      "
              />
            </g>
          </g>
          <g>
            <g>
              <polygon
                class="st2"
                points="731.1,405.9 647.1,454.4 731.1,502.9 815,454.4      "
              />
            </g>
          </g>
          <g>
            <g>
              <polygon
                class="st2"
                points="815.1,454.4 731.1,502.9 815.1,551.4 899,502.9      "
              />
            </g>
          </g>
          <g>
            <g>
              <polygon
                class="st2"
                points="899.1,502.8 815.1,551.3 899.1,599.8 983,551.3      "
              />
            </g>
          </g>
          <g>
            <g>
              <polygon
                class="st2"
                points="983,551.4 899,599.9 983,648.4 1067,599.9      "
              />
            </g>
          </g>
        </g>
        <g>
          <g class="st9">
            <path
              class="st10"
              d="M1155.7,404.1c-4.9,2.9-6.6,5.8-6.5,9.7c0.1,3.3-1.9,6.2-2.7,9.4c-0.8,3.3-2,5.9,0.4,9.1      c1.9,2.6,5.3,5.2,9.4,6.7c2.9,1.1,5.8,0.8,8.8,1.4c0.8,0.3,1.5,0.6,2.3,0.9c1.4,0.7,2.3,1.7,3.8,2.5c3.4,2.1,7,3.6,12.2,3.3      c6-0.4,11.3-1.5,17-2.7c4.8-1,9.7-0.8,14.2-2.4c1-0.4,1.9-0.8,2.7-1.2c3.5-2,4.9-4.8,7.7-7.3c2.7-2.5,7.7-5,7.8-8.2      c0.1-1.7-1.6-3.4-2.1-5c-0.4-1.4-0.8-2.9-1.2-4.3c-0.3-1.2-0.4-2.6-0.7-3.9c0.2-0.5,0.3-1,0.3-1.5c-0.1-3.3-4.7-5.9-9.6-7.2      c-2.4-0.7-5.2-0.9-7.8-1.2c-3.2-1.3-6.6-2.3-10.2-3c-4.2-2-8.2-3.6-14.3-3.5c-6.7,0-11.3,3.2-17.2,4.7      C1165,401.7,1159.8,401.7,1155.7,404.1z"
            />
          </g>
          <g>
            <g>
              <path
                class="st11"
                d="M1188.5,405.4c0.5-2.5,0.2-8.1,0.3-12.1c0-2,0.2-4,0.4-5.9c1.1,0.2,2.2,0.3,3.3,0.4       c-0.4,0.6-0.6,1.3-0.8,2.1c-0.9,3.7-1.1,7.8-0.9,11.5c0.2,2.9,0.6,5.9,0.6,8.8c0,1.9,0,3.6,0.7,5.4c0.8,1.9,2.1,3.6,3.7,4.9       c0,0.1,0.1,0.1,0.1,0.2c0.1,0.1,0.1,0.2,0.2,0.4c-0.7-0.3-1.5-0.5-2.2-0.4c-0.6,0.2-0.7,0.8-1.1,1c-1.4,0.8-2,0.3-3.3,1.3       c-0.5,0.4-1.1,0.7-1.6,0.8c0-0.5,0-1-0.1-1.4c-0.2-1.8-0.1-4.6-0.3-7.7C1187.2,411.8,1188,407.9,1188.5,405.4z"
              />
              <path
                class="st12"
                d="M1180.6,422.1c0.2-0.2,0.4-0.3,0.5-0.5c0.1-0.1,0.1-0.2,0.2-0.2c0.4-0.8,0.9-1.5,1.3-2.2       c0.6-0.9,1.1-1.7,1.5-2.7c1-2.2,1.4-4.7,1.7-7.1c0-0.2,0.1-0.5,0.1-0.7c0.1-0.3,0.2-0.6,0.2-0.9c1.8-5.5,0.8-11.6,1.4-17.3       c0.2-1.1,0.5-2.1,0.7-3.2c0.3,0.1,0.6,0.1,0.9,0.2c-0.2,1.9-0.4,3.9-0.4,5.9c-0.1,3.9,0.2,9.6-0.3,12.1s-1.3,6.4-1,9.5       c0.2,3.1,0.2,5.8,0.3,7.7c0,0.3,0.1,0.8,0.1,1.4c-1,0.3-2,0.1-2.4-1.4c-0.1-0.3-0.3-0.6-0.6-0.6c-1.4-0.1-2.6,1.1-3.8,1.1       c0.1,0-0.4-0.2-0.8-0.3C1180.3,422.5,1180.5,422.3,1180.6,422.1z"
              />
            </g>
            <g>
              <path
                class="st13"
                d="M1198.2,407.9c-1.6-0.2-3.9,0.4-5.4-0.2c-1-0.3-1.8-1.6-2.7-1.8c-1.4-0.4-3.5,0.7-5.2,1       c-1.9,0.2-3.9-0.1-5.7-0.4c-2.2-0.4-3.2-0.8-4.2-2.4c-1.8,0-3.2,0.2-4.9-1.1c-2.1-1.7-4.5-5.2-6.2-7.4       c-1.5-1.9-1.5-2.5-2.3-4.7c-1.2-3-2.4-4.1-4.7-6.2c-2.2-2-3.8-4.1-4.5-7.1c-0.6-2.5-0.1-4.4-1.2-6.8c-1.2-2.6-2.6-3.7-2.3-6.7       c0.1-1.3,0.6-2.5,1.2-3.7c0.1,1.1,0.1,2.2,0.4,3.4c0.7,3,2.4,5.1,4.5,7.1c2.3,2.1,3.5,3.3,4.7,6.2c0.9,2.2,0.9,2.8,2.3,4.7       c1.7,2.2,4.1,5.7,6.2,7.4c1.6,1.3,3,1.1,4.9,1.1c0.9,1.6,2,1.9,4.2,2.4c1.8,0.4,3.8,0.7,5.7,0.4c1.8-0.2,3.8-1.3,5.2-1       c0.9,0.2,1.7,1.5,2.7,1.8c1.5,0.5,3.9,0,5.4,0.2c2.9,0.3,4.3,0.6,6.9-1.4c1.6-1.3,2.5-3.4,4-4.7c1.6-1.4,3.2-1.1,4.8-1.9       c1.9-1,3.7-2.5,5.3-3.9c1.9-1.7,3.3-3.8,5.1-5.6c1.1-1.1,2.8-2,3.6-3.4c1.2-2.1,1.1-4.6,1.8-6.8c0.7-2.3,1-4.7,1.1-7       c1.9,6.9,2.9,13.9,0.8,20.8c-0.7,2.2-0.6,4.7-1.8,6.8c-0.8,1.4-2.5,2.3-3.6,3.4c-1.8,1.8-3.2,3.9-5.1,5.6       c-1.6,1.5-3.4,2.9-5.3,3.9c-1.6,0.8-3.2,0.5-4.8,1.9c-1.5,1.2-2.4,3.4-4,4.7C1202.5,408.5,1201.1,408.2,1198.2,407.9z"
              />
              <g>
                <path
                  class="st14"
                  d="M1206.9,333.1c-4.3-1.7-7.3-4.2-12-4.6c-3.4-0.3-7.5,0-10.7,1c-3,1-6.2,3.2-8.6,5.1        c-3.2,2.6-6.7,5.2-9.5,8.2c-2.4,2.6-5,3.7-7.4,5.9c-2.3,2.1-4.2,5.7-5.9,8.5c-0.7,0.5-1.4,1-1.8,1.5c-0.4,0.6-0.6,1.3-0.9,2        c0-0.1,0-0.2,0-0.4c0.6-1.1,1.2-2.1,1.8-3.2c0.6-1.2,0.8-2.7,1.5-3.9c0.4-0.6,1-1,1.7-1.5c1.6-2.8,3.3-6.4,5.5-8.5        c2.3-2.2,4.7-3.3,6.9-5.9c2.6-3,5.8-5.6,8.8-8.2c2.2-1.9,5.1-4.1,8-5.1c3-1.1,6.8-1.3,9.9-1c4.3,0.4,7.1,3,11.1,4.6        c5.8,2.4,10.6,6.6,15,11c2.4,2.5,3.9,5.1,5,8c-0.7-0.9-1.4-1.7-2.3-2.5C1218.3,339.6,1213.1,335.5,1206.9,333.1z"
                />
                <path
                  class="st15"
                  d="M1228.9,359.4c-0.1,2.4-0.4,4.7-1.1,7c-0.7,2.2-0.6,4.7-1.8,6.8c-0.8,1.4-2.5,2.3-3.6,3.4        c-1.8,1.8-3.2,3.9-5.1,5.6c-1.6,1.5-3.4,2.9-5.3,3.9c-1.6,0.8-3.2,0.5-4.8,1.9c-1.5,1.2-2.4,3.4-4,4.7c-2.6,2.1-4,1.7-6.9,1.4        c-1.6-0.2-3.9,0.4-5.4-0.2c-1-0.3-1.8-1.6-2.7-1.8c-1.4-0.4-3.5,0.7-5.2,1c-1.9,0.2-3.9-0.1-5.7-0.4c-2.2-0.4-3.2-0.8-4.2-2.4        c-1.8,0-3.2,0.2-4.9-1.1c-2.1-1.7-4.5-5.2-6.2-7.4c-1.5-1.9-1.5-2.5-2.3-4.7c-1.2-3-2.4-4.1-4.7-6.2c-2.2-2-3.8-4.1-4.5-7.1        c-0.3-1.1-0.3-2.1-0.4-3c0.2-0.7,0.5-1.4,0.9-2s1.1-1,1.8-1.5c1.7-2.8,3.6-6.4,5.9-8.5c2.4-2.2,5.1-3.3,7.4-5.9        c2.8-3,6.3-5.6,9.5-8.2c2.4-1.9,5.5-4.1,8.6-5.1c3.2-1.1,7.3-1.3,10.7-1c4.7,0.4,7.7,3,12,4.6c6.2,2.4,11.4,6.6,16.1,11        c0.9,0.8,1.6,1.7,2.3,2.5c1.4,3.7,2.1,7.7,3.3,11.8C1228.7,358.8,1228.8,359.1,1228.9,359.4z"
                />
              </g>
            </g>
          </g>
        </g>
      </g>
      <g>
        <polygon
          class="st16"
          points="925.8,338.8 783.7,420.8 892,482.9 913.1,470.7 1035.3,542 1156.7,471.8   "
        />
        <polygon
          class="st16"
          points="819.5,441.5 722.9,498.2 779.8,531.4 876.9,474.4   "
        />
        <polygon
          class="st17"
          points="1128.7,402 1128.7,473.6 1039.6,525.4 1039.5,453.7   "
        />
        <polygon
          class="st18"
          points="1039.5,453.7 974.5,416.3 974.5,360.1 913.8,325.5 913.8,453.1 1039.6,525.4   "
        />
        <polygon
          class="st17"
          points="1063.6,308.1 974.5,360.1 974.5,416.3 974.5,421.5 1063.6,370.1   "
        />
        <polygon
          class="st17"
          points="913.8,325.5 889.5,339.5 889.5,467.3 913.8,453.1 913.8,381.3   "
        />
        <polygon
          class="st18"
          points="889.5,467.3 889.5,339.5 807.8,292.5 807.8,420 811.1,421.9 811.1,421.9 821.3,427.8 826.8,424.6     872.7,451.1 872.7,457.7   "
        />
        <polygon
          class="st19"
          points="1054.7,304.6 978.7,348.8 978.7,402.3 1054.7,358.4   "
        />
        <polygon
          class="st20"
          points="1054.7,358.4 978.7,402.3 978.7,403.6 1055.8,359   "
        />
        <polygon
          class="st21"
          points="1054.7,304.6 1054.7,358.4 1055.8,359 1055.8,304   "
        />
        <polygon
          class="st21"
          points="1026.8,320.7 1026.8,374.5 1027.9,375.1 1027.9,320.1   "
        />
        <polygon
          class="st21"
          points="1002.1,335 1002.1,388.8 1003.2,389.5 1003.2,334.4   "
        />
        <polygon
          class="st22"
          points="1055.8,304 1055.8,359 1029.6,374.1 1029.7,319.1 1027.9,320.2 1027.9,375.1 1004.9,388.4     1004.9,333.4 1003.2,334.5 1003.2,389.4 978.7,403.6 978.7,404.7 1056.8,359.6 1056.8,303.4   "
        />
        <polygon
          class="st19"
          points="978.7,408 1059.6,361.3 1056.8,359.6 978.7,404.7   "
        />
        <polygon
          class="st23"
          points="1056.8,303.4 1056.8,359.6 1059.7,361.3 1059.7,301.7   "
        />
        <polygon
          class="st19"
          points="970.3,350.7 970.3,404.2 923,376.9 923,323.4   "
        />
        <polygon
          class="st20"
          points="923.1,376.1 922,376.7 970.3,404.8 970.3,403.3   "
        />
        <polygon
          class="st21"
          points="922,376.7 923.1,376.1 923.1,323.4 922,322.8   "
        />
        <polygon
          class="st22"
          points="922,376.7 922,322.8 921,322.2 921,377.3 970.3,405.7 970.3,404.6   "
        />
        <polygon
          class="st19"
          points="970.3,409.1 918.2,379 921,377.3 970.3,405.7   "
        />
        <polygon
          class="st21"
          points="826.8,370.1 872.7,396.7 872.7,451.1 826.8,424.6   "
        />
        <polygon
          class="st20"
          points="872.7,403.8 826.8,377.3 826.8,375 872.7,401.6   "
        />
        <polygon
          class="st20"
          points="872.7,409.9 826.8,383.4 826.8,381.1 872.7,407.7   "
        />
        <polygon
          class="st20"
          points="872.7,416 826.8,389.5 826.8,387.2 872.7,413.8   "
        />
        <polygon
          class="st20"
          points="872.7,422.1 826.8,395.5 826.8,393.3 872.7,419.9   "
        />
        <polygon
          class="st20"
          points="872.7,428.2 826.8,401.6 826.8,399.4 872.7,426   "
        />
        <polygon
          class="st20"
          points="872.7,434.3 826.8,407.7 826.8,405.5 872.7,432   "
        />
        <polygon
          class="st20"
          points="872.7,440.4 826.8,413.8 826.8,411.6 872.7,438.1   "
        />
        <polygon
          class="st20"
          points="872.7,446.5 826.8,419.9 826.8,417.7 872.7,444.2   "
        />
        <polygon
          class="st19"
          points="1089.5,437.6 1089.5,479.4 1118.3,462.8 1118.3,420.9   "
        />
        <polygon
          class="st20"
          points="1089.5,476.4 1115.6,461.3 1117,462.1 1089.5,478   "
        />
        <polygon
          class="st21"
          points="1115.6,422.4 1115.6,461.3 1117,462.1 1117,421.6   "
        />
        <polygon
          class="st22"
          points="1117,421.6 1117,462.1 1089.5,478 1089.5,479.4 1118.3,462.8 1118.3,420.9   "
        />
        <polygon
          class="st19"
          points="1089.5,479.4 1118.3,462.8 1121.2,464.6 1089.5,482.9   "
        />
        <polygon
          class="st23"
          points="1121.2,419.2 1121.2,464.6 1118.3,462.8 1118.3,420.9   "
        />
        <polygon
          class="st19"
          points="1050.6,460.2 1050.6,502 1079.4,485.4 1079.4,443.5   "
        />
        <polygon
          class="st20"
          points="1050.6,499 1076.8,483.9 1078.1,484.7 1050.6,500.6   "
        />
        <polygon
          class="st21"
          points="1076.7,445 1076.8,483.9 1078.1,484.7 1078.2,444.2   "
        />
        <polygon
          class="st22"
          points="1078.2,444.2 1078.1,484.7 1050.6,500.6 1050.6,502 1079.4,485.4 1079.4,443.5   "
        />
        <polygon
          class="st19"
          points="1050.6,502 1079.4,485.4 1082.3,487.1 1050.6,505.5   "
        />
        <polygon
          class="st23"
          points="1082.3,441.8 1082.3,487.1 1079.4,485.4 1079.4,443.5   "
        />
        <polygon
          class="st19"
          points="1020.8,454.6 1020.8,496.7 992.2,480.2 992,438   "
        />
        <polygon
          class="st20"
          points="1020.8,494.6 994.6,479.6 993.3,480.4 1020.8,496.2   "
        />
        <polygon
          class="st21"
          points="994.6,459.7 994.7,439.5 993.2,438.7 993.3,459.9 993.2,459.9 993.3,480.4 994.6,479.6 994.6,459.8       "
        />
        <polygon
          class="st22"
          points="993.2,438.7 993.3,480.4 1020.8,496.2 1020.8,497.6 992,481.1 992,438   "
        />
        <polygon
          class="st19"
          points="1020.8,497.6 992,481.1 989.1,482.8 1020.8,501.1   "
        />
        <polygon
          class="st19"
          points="965.4,415.6 933.2,397 933,462.3 965.2,482.6   "
        />
        <polygon
          class="st20"
          points="946.3,470.5 947.6,469.7 933.2,461.4 931.9,462.2   "
        />
        <polygon
          class="st20"
          points="963,480.1 964.3,479.3 949.5,470.9 948.3,471.6   "
        />
        <polygon
          class="st21"
          points="931.9,396.3 931.9,462.2 933.2,461.4 933.2,397   "
        />
        <polygon
          class="st21"
          points="949.5,406.4 948.3,405.7 948.3,471.6 949.5,470.9   "
        />
        <polygon
          class="st22"
          points="963,414.2 963,480.1 948.3,471.6 948.3,405.7 946.3,404.6 946.3,470.5 931.9,462.2 931.9,396.3     930.7,395.6 930.7,462.8 965.2,482.6 965.4,415.6   "
        />
        <polygon
          class="st19"
          points="816,297.1 815.9,343.3 884.8,383.2 884.8,336.7   "
        />
        <polygon
          class="st20"
          points="816,342.5 814.9,343.2 884.8,383.6 884.8,382.3   "
        />
        <polygon
          class="st21"
          points="814.9,296.4 814.9,343.2 816,342.5 816,297.1   "
        />
        <polygon
          class="st21"
          points="838.2,309.8 838.2,356.6 839.3,356 839.3,310.5   "
        />
        <polygon
          class="st21"
          points="862.9,324.1 862.9,371 864,370.3 864,324.7   "
        />
        <polygon
          class="st22"
          points="862.9,370.9 862.9,324.1 861.1,323.1 861.1,369.9 838.2,356.6 838.2,309.8 836.4,308.8 836.4,355.6     814.9,343.2 814.9,296.4 813.9,295.8 813.9,343.7 884.8,384.7 884.8,383.6   "
        />
        <polygon
          class="st19"
          points="884.8,384.7 813.9,343.7 811.1,345.4 884.8,388   "
        />
        <polygon
          class="st21"
          points="1069.2,300.4 1069.2,308.7 972.6,364.8 972.6,356.6   "
        />
        <polygon
          class="st22"
          points="914.9,323.2 972.6,356.6 972.6,364.8 914.9,331.5   "
        />
        <polygon
          class="st21"
          points="914.9,323.2 914.9,331.5 884.3,349.3 884.3,341   "
        />
        <polygon
          class="st22"
          points="799.2,291.8 884.3,341 884.3,349.3 799.2,300.1   "
        />
        <polygon
          class="st19"
          points="1059.3,372.6 979.1,418.9 1040.6,454.3 1120.8,408.1   "
        />
        <polygon
          class="st22"
          points="909.8,383.5 909.8,390.6 1040.6,466 1040.6,459   "
        />
        <polygon
          class="st21"
          points="1134.1,411.7 1040.6,466 1040.6,459 1134.1,405   "
        />
        <polygon
          class="st20"
          points="1040.6,454.3 913.8,381.3 909.8,383.5 1040.6,459   "
        />
        <polygon
          class="st20"
          points="1134.1,405 1063.6,364.5 1059.3,366.9 1125.7,405.2   "
        />
        <polygon
          class="st20"
          points="1125.7,405.2 1040.6,454.3 1040.6,459 1134.1,405   "
        />
        <polygon
          class="st22"
          points="1059.3,366.9 1059.3,372.6 1120.8,408.1 1125.7,405.2   "
        />
        <polygon
          class="st20"
          points="926.8,218.2 1069.2,300.4 972.6,356.6 914.9,323.2 884.3,341 799.2,291.8   "
        />
        <polygon
          class="st19"
          points="927,222.5 1062,300.4 972.6,352.1 914.9,318.7 884.3,336.5 806.9,291.8   "
        />
        <polygon
          class="st22"
          points="927,222.5 927,228.1 1057,303.3 1062,300.4   "
        />
        <polygon
          class="st21"
          points="927,222.5 927,228.1 811.9,294.7 806.9,291.8   "
        />
      </g>
      <g>
        <g class="st9">
          <path
            class="st10"
            d="M997.7,553c-2,1.2-2.7,2.3-2.6,3.9c0,1.3-0.8,2.5-1.1,3.8s-0.8,2.4,0.2,3.7c0.8,1.1,2.1,2.1,3.8,2.7     c1.2,0.4,2.4,0.3,3.6,0.6c0.3,0.1,0.6,0.2,0.9,0.3c0.6,0.3,0.9,0.7,1.5,1c1.4,0.8,2.9,1.5,4.9,1.4c2.4-0.2,4.6-0.6,6.9-1.1     c1.9-0.4,3.9-0.3,5.8-1c0.4-0.1,0.8-0.3,1.1-0.5c1.4-0.8,2-2,3.1-3s3.1-2,3.2-3.3c0-0.7-0.7-1.4-0.8-2c-0.2-0.6-0.3-1.2-0.5-1.7     c-0.1-0.5-0.2-1-0.3-1.6c0.1-0.2,0.1-0.4,0.1-0.6c-0.1-1.4-1.9-2.4-3.9-2.9c-1-0.3-2.1-0.4-3.2-0.5c-1.3-0.5-2.7-0.9-4.1-1.2     c-1.7-0.8-3.3-1.4-5.8-1.4c-2.7,0-4.6,1.3-7,1.9C1001.4,552,999.3,552,997.7,553z"
          />
        </g>
        <g>
          <g>
            <path
              class="st11"
              d="M1011,553.5c0.2-1,0.1-3.3,0.1-4.9c0-0.8,0.1-1.6,0.2-2.4c0.4,0.1,0.9,0.1,1.3,0.2      c-0.1,0.2-0.3,0.5-0.3,0.8c-0.4,1.5-0.4,3.2-0.3,4.7c0.1,1.2,0.2,2.4,0.3,3.6c0,0.8,0,1.5,0.3,2.2c0.3,0.8,0.9,1.4,1.5,2      c0,0,0,0,0,0.1s0.1,0.1,0.1,0.2c-0.3-0.1-0.6-0.2-0.9-0.2c-0.3,0.1-0.3,0.3-0.5,0.4c-0.6,0.3-0.8,0.1-1.4,0.5      c-0.2,0.2-0.4,0.3-0.7,0.3c0-0.2,0-0.4,0-0.6c-0.1-0.7,0-1.9-0.1-3.1S1010.8,554.5,1011,553.5z"
            />
            <path
              class="st12"
              d="M1007.8,560.3c0.1-0.1,0.2-0.1,0.2-0.2l0.1-0.1c0.1-0.3,0.4-0.6,0.5-0.9c0.2-0.3,0.4-0.7,0.6-1.1      c0.4-0.9,0.6-1.9,0.7-2.9c0-0.1,0-0.2,0-0.3s0.1-0.2,0.1-0.4c0.7-2.2,0.3-4.7,0.6-7c0.1-0.4,0.2-0.9,0.3-1.3      c0.1,0,0.2,0,0.4,0.1c-0.1,0.8-0.1,1.6-0.2,2.4c0,1.6,0.1,3.9-0.1,4.9c-0.2,1-0.5,2.6-0.4,3.8s0.1,2.4,0.1,3.1      c0,0.1,0,0.3,0,0.6c-0.4,0.1-0.8,0-1-0.6c0-0.1-0.1-0.2-0.3-0.2c-0.6-0.1-1.1,0.4-1.5,0.4c0,0-0.2-0.1-0.3-0.1      C1007.7,560.5,1007.7,560.4,1007.8,560.3z"
            />
          </g>
          <g>
            <path
              class="st13"
              d="M1014.9,554.5c-0.6-0.1-1.6,0.1-2.2-0.1c-0.4-0.1-0.7-0.6-1.1-0.7c-0.6-0.2-1.4,0.3-2.1,0.4      c-0.8,0.1-1.6,0-2.3-0.2c-0.9-0.2-1.3-0.3-1.7-1c-0.7,0-1.3,0.1-2-0.5c-0.9-0.7-1.8-2.1-2.5-3c-0.6-0.8-0.6-1-1-1.9      c-0.5-1.2-1-1.7-1.9-2.5s-1.6-1.7-1.8-2.9c-0.2-1,0-1.8-0.5-2.8s-1.1-1.5-0.9-2.7c0-0.5,0.2-1,0.5-1.5c0,0.4,0,0.9,0.2,1.4      c0.3,1.2,1,2.1,1.8,2.9c0.9,0.9,1.4,1.3,1.9,2.5c0.4,0.9,0.4,1.1,1,1.9c0.7,0.9,1.7,2.3,2.5,3c0.7,0.5,1.2,0.5,2,0.5      c0.4,0.6,0.8,0.8,1.7,1c0.7,0.2,1.6,0.3,2.3,0.2s1.5-0.5,2.1-0.4c0.4,0.1,0.7,0.6,1.1,0.7c0.6,0.2,1.6,0,2.2,0.1      c1.2,0.1,1.7,0.3,2.8-0.6c0.7-0.5,1-1.4,1.6-1.9c0.7-0.5,1.3-0.4,2-0.8c0.8-0.4,1.5-1,2.2-1.6c0.8-0.7,1.3-1.6,2.1-2.3      c0.5-0.4,1.1-0.8,1.5-1.4c0.5-0.9,0.4-1.9,0.7-2.8c0.3-0.9,0.4-1.9,0.4-2.9c0.8,2.8,1.2,5.6,0.3,8.4c-0.3,0.9-0.2,1.9-0.7,2.8      c-0.3,0.6-1,0.9-1.5,1.4c-0.7,0.7-1.3,1.6-2.1,2.3c-0.7,0.6-1.4,1.2-2.2,1.6c-0.6,0.3-1.3,0.2-2,0.8c-0.6,0.5-1,1.4-1.6,1.9      C1016.7,554.8,1016.1,554.7,1014.9,554.5z"
            />
            <g>
              <path
                class="st14"
                d="M1018.5,524.1c-1.7-0.7-3-1.7-4.9-1.9c-1.4-0.1-3,0-4.3,0.4c-1.2,0.4-2.5,1.3-3.5,2.1       c-1.3,1-2.7,2.1-3.9,3.3c-1,1.1-2,1.5-3,2.4c-0.9,0.8-1.7,2.3-2.4,3.5c-0.3,0.2-0.6,0.4-0.7,0.6c-0.2,0.2-0.3,0.5-0.4,0.8v-0.1       c0.2-0.4,0.5-0.9,0.7-1.3c0.2-0.5,0.3-1.1,0.6-1.6c0.1-0.2,0.4-0.4,0.7-0.6c0.7-1.1,1.4-2.6,2.2-3.5c0.9-0.9,1.9-1.4,2.8-2.4       c1-1.2,2.4-2.3,3.6-3.3c0.9-0.8,2.1-1.7,3.2-2.1c1.2-0.4,2.8-0.5,4-0.4c1.8,0.2,2.9,1.2,4.5,1.9c2.3,1,4.3,2.7,6.1,4.5       c1,1,1.6,2.1,2,3.2c-0.3-0.4-0.6-0.7-0.9-1C1023.1,526.8,1021,525.1,1018.5,524.1z"
              />
              <path
                class="st15"
                d="M1027.4,534.8c0,1-0.2,1.9-0.4,2.9c-0.3,0.9-0.2,1.9-0.7,2.8c-0.3,0.6-1,0.9-1.5,1.4       c-0.7,0.7-1.3,1.6-2.1,2.3c-0.7,0.6-1.4,1.2-2.2,1.6c-0.6,0.3-1.3,0.2-2,0.8c-0.6,0.5-1,1.4-1.6,1.9c-1.1,0.8-1.6,0.7-2.8,0.6       c-0.6-0.1-1.6,0.1-2.2-0.1c-0.4-0.1-0.7-0.6-1.1-0.7c-0.6-0.2-1.4,0.3-2.1,0.4c-0.8,0.1-1.6,0-2.3-0.2c-0.9-0.2-1.3-0.3-1.7-1       c-0.7,0-1.3,0.1-2-0.5c-0.9-0.7-1.8-2.1-2.5-3c-0.6-0.8-0.6-1-1-1.9c-0.5-1.2-1-1.7-1.9-2.5s-1.6-1.7-1.8-2.9       c-0.1-0.4-0.1-0.8-0.2-1.2c0.1-0.3,0.2-0.6,0.4-0.8s0.5-0.4,0.7-0.6c0.7-1.1,1.5-2.6,2.4-3.5c1-0.9,2.1-1.4,3-2.4       c1.1-1.2,2.5-2.3,3.9-3.3c1-0.8,2.2-1.7,3.5-2.1c1.3-0.4,3-0.5,4.3-0.4c1.9,0.2,3.1,1.2,4.9,1.9c2.5,1,4.6,2.7,6.5,4.5       c0.4,0.3,0.7,0.7,0.9,1c0.6,1.5,0.9,3.1,1.3,4.8C1027.3,534.6,1027.4,534.7,1027.4,534.8z"
              />
            </g>
          </g>
        </g>
      </g>
      <g>
        <g class="st9">
          <path
            class="st10"
            d="M902.4,498.7c-3,1.7-4,3.5-3.9,5.8c0.1,2-1.1,3.7-1.6,5.6c-0.5,2-1.2,3.5,0.2,5.4c1.1,1.6,3.2,3.1,5.6,4     c1.7,0.6,3.5,0.5,5.3,0.9c0.5,0.2,0.9,0.4,1.4,0.5c0.8,0.4,1.4,1,2.3,1.5c2.1,1.2,4.2,2.2,7.3,2c3.6-0.2,6.8-0.9,10.2-1.6     c2.9-0.6,5.8-0.5,8.5-1.4c0.6-0.2,1.2-0.5,1.6-0.7c2.1-1.2,2.9-2.9,4.6-4.4c1.6-1.5,4.6-3,4.7-4.9c0.1-1-1-2-1.2-3     c-0.2-0.9-0.5-1.7-0.7-2.6c-0.2-0.7-0.2-1.5-0.4-2.3c0.1-0.3,0.2-0.6,0.2-0.9c-0.1-2-2.8-3.5-5.8-4.3c-1.5-0.4-3.1-0.5-4.7-0.7     c-1.9-0.8-4-1.4-6.1-1.8c-2.5-1.2-4.9-2.1-8.6-2.1c-4,0-6.8,1.9-10.3,2.8C907.9,497.2,904.8,497.2,902.4,498.7z"
          />
        </g>
        <g>
          <g>
            <path
              class="st11"
              d="M922,499.5c0.3-1.5,0.1-4.9,0.2-7.2c0-1.2,0.1-2.4,0.2-3.5c0.7,0.1,1.3,0.2,2,0.3      c-0.2,0.4-0.4,0.8-0.5,1.2c-0.6,2.2-0.7,4.7-0.5,6.9c0.1,1.8,0.3,3.5,0.4,5.3c0,1.1,0,2.2,0.4,3.2c0.5,1.2,1.3,2.1,2.2,3      c0,0,0,0.1,0.1,0.1c0,0.1,0.1,0.1,0.1,0.2c-0.4-0.2-0.9-0.3-1.3-0.2c-0.4,0.1-0.4,0.5-0.7,0.6c-0.9,0.5-1.2,0.2-2,0.8      c-0.3,0.2-0.6,0.4-1,0.5c0-0.3,0-0.6,0-0.8c-0.1-1.1,0-2.8-0.2-4.6C921.3,503.3,921.8,500.9,922,499.5z"
            />
            <path
              class="st12"
              d="M917.3,509.5c0.1-0.1,0.2-0.2,0.3-0.3c0-0.1,0.1-0.1,0.1-0.1c0.2-0.5,0.5-0.9,0.8-1.3      c0.3-0.5,0.7-1,0.9-1.6c0.6-1.3,0.8-2.8,1-4.3c0-0.1,0-0.3,0.1-0.4c0-0.2,0.1-0.3,0.1-0.5c1.1-3.3,0.5-6.9,0.8-10.4      c0.1-0.6,0.3-1.3,0.4-1.9c0.2,0,0.4,0.1,0.5,0.1c-0.1,1.1-0.2,2.4-0.2,3.5c0,2.4,0.1,5.7-0.2,7.2s-0.8,3.8-0.6,5.7      c0.1,1.8,0.1,3.5,0.2,4.6c0,0.2,0,0.5,0,0.8c-0.6,0.2-1.2,0-1.4-0.9c-0.1-0.2-0.2-0.3-0.4-0.4c-0.9-0.1-1.6,0.7-2.3,0.7      c0,0-0.2-0.1-0.5-0.2C917.2,509.7,917.2,509.6,917.3,509.5z"
            />
          </g>
          <g>
            <path
              class="st13"
              d="M927.9,500.9c-0.9-0.1-2.3,0.2-3.3-0.1c-0.6-0.2-1.1-1-1.6-1.1c-0.9-0.2-2.1,0.4-3.1,0.6      c-1.1,0.1-2.3,0-3.4-0.3c-1.3-0.3-1.9-0.5-2.5-1.4c-1.1,0-1.9,0.1-2.9-0.7c-1.3-1-2.7-3.1-3.7-4.4c-0.9-1.1-0.9-1.5-1.4-2.8      c-0.7-1.8-1.4-2.5-2.8-3.7c-1.3-1.2-2.3-2.5-2.7-4.3c-0.4-1.5-0.1-2.7-0.7-4.1c-0.7-1.5-1.6-2.2-1.4-4c0.1-0.8,0.4-1.5,0.7-2.2      c0.1,0.6,0.1,1.3,0.2,2c0.4,1.8,1.4,3,2.7,4.3c1.4,1.3,2.1,2,2.8,3.7c0.5,1.3,0.5,1.7,1.4,2.8c1,1.3,2.5,3.4,3.7,4.4      c1,0.8,1.8,0.7,2.9,0.7c0.5,0.9,1.2,1.2,2.5,1.4c1.1,0.2,2.3,0.4,3.4,0.3c1.1-0.1,2.3-0.8,3.1-0.6c0.5,0.1,1,0.9,1.6,1.1      c0.9,0.3,2.3,0,3.3,0.1c1.8,0.2,2.6,0.4,4.1-0.9c1-0.8,1.5-2,2.4-2.8c1-0.8,1.9-0.6,2.9-1.1c1.1-0.6,2.2-1.5,3.2-2.3      c1.1-1,2-2.3,3.1-3.4c0.7-0.7,1.7-1.2,2.2-2c0.7-1.3,0.7-2.7,1.1-4.1c0.4-1.4,0.6-2.8,0.6-4.2c1.1,4.1,1.7,8.3,0.5,12.5      c-0.4,1.3-0.3,2.8-1.1,4.1c-0.5,0.9-1.5,1.4-2.2,2c-1.1,1.1-1.9,2.3-3.1,3.4c-1,0.9-2,1.8-3.2,2.3c-0.9,0.5-1.9,0.3-2.9,1.1      c-0.9,0.7-1.4,2-2.4,2.8C930.5,501.3,929.6,501.1,927.9,500.9z"
            />
            <g>
              <path
                class="st14"
                d="M933.1,456.1c-2.6-1-4.4-2.5-7.2-2.8c-2-0.2-4.5,0-6.4,0.6c-1.8,0.6-3.7,1.9-5.1,3       c-1.9,1.5-4,3.1-5.7,4.9c-1.4,1.6-3,2.2-4.5,3.6c-1.4,1.2-2.5,3.4-3.5,5.1c-0.4,0.3-0.8,0.6-1.1,0.9c-0.2,0.3-0.4,0.8-0.5,1.2       c0-0.1,0-0.1,0-0.2c0.3-0.6,0.7-1.3,1.1-1.9c0.4-0.7,0.5-1.6,0.9-2.3c0.2-0.3,0.6-0.6,1-0.9c1-1.7,2-3.9,3.3-5.1       c1.4-1.3,2.8-2,4.1-3.6c1.5-1.8,3.5-3.4,5.3-4.9c1.3-1.1,3.1-2.5,4.8-3c1.8-0.6,4.1-0.8,6-0.6c2.6,0.2,4.3,1.8,6.7,2.8       c3.5,1.5,6.4,3.9,9,6.6c1.5,1.5,2.3,3.1,3,4.8c-0.4-0.5-0.8-1-1.4-1.5C939.9,460,936.8,457.5,933.1,456.1z"
              />
              <path
                class="st15"
                d="M946.2,471.9c0,1.4-0.2,2.8-0.6,4.2c-0.4,1.3-0.3,2.8-1.1,4.1c-0.5,0.9-1.5,1.4-2.2,2       c-1.1,1.1-1.9,2.3-3.1,3.4c-1,0.9-2,1.8-3.2,2.3c-0.9,0.5-1.9,0.3-2.9,1.1c-0.9,0.7-1.4,2-2.4,2.8c-1.6,1.2-2.4,1-4.1,0.9       c-0.9-0.1-2.3,0.2-3.3-0.1c-0.6-0.2-1.1-1-1.6-1.1c-0.9-0.2-2.1,0.4-3.1,0.6c-1.1,0.1-2.3,0-3.4-0.3c-1.3-0.3-1.9-0.5-2.5-1.4       c-1.1,0-1.9,0.1-2.9-0.7c-1.3-1-2.7-3.1-3.7-4.4c-0.9-1.1-0.9-1.5-1.4-2.8c-0.7-1.8-1.4-2.5-2.8-3.7c-1.3-1.2-2.3-2.5-2.7-4.3       c-0.2-0.7-0.2-1.2-0.2-1.8c0.1-0.4,0.3-0.8,0.5-1.2c0.2-0.3,0.7-0.6,1.1-0.9c1-1.7,2.2-3.9,3.5-5.1c1.5-1.3,3-2,4.5-3.6       c1.6-1.8,3.8-3.4,5.7-4.9c1.4-1.1,3.3-2.5,5.1-3c1.9-0.6,4.4-0.8,6.4-0.6c2.8,0.2,4.6,1.8,7.2,2.8c3.7,1.5,6.8,3.9,9.7,6.6       c0.5,0.5,1,1,1.4,1.5c0.8,2.2,1.3,4.6,2,7.1C946.1,471.5,946.2,471.7,946.2,471.9z"
              />
            </g>
          </g>
        </g>
      </g>
      <polygon class="st7" points="1318.5,454.6 1318.5,468.2 1306.8,461.4  " />
      <polygon class="st7" points="658.9,461.1 647.2,468 647.1,454.4  " />
      <polyline
        class="st16"
        points="779.8,529.9 780.6,545.6 706.1,502 722.9,498.2  "
      />
      <g>
        <g>
          <polygon
            class="st2"
            points="766.3,590.1 747.9,600.7 926.7,702.7 945.1,692.1    "
          />
        </g>
      </g>
      <g>
        <ellipse class="st60" cx="911.1" cy="683.5" rx="11.5" ry="4.9" />
        <g>
          <g class="st61">
            <path
              class="st62"
              d="M916.9,682c0,0.1,0,0.1,0,0.2c0,0.1,0,0.1,0,0.2v0.1c0,0.1,0,0.1,0,0.2c0,0,0,0.1-0.1,0.1     c0,0.1-0.1,0.1-0.1,0.2l-0.1,0.1c0,0.1-0.1,0.1-0.1,0.2l-0.1,0.1c-0.1,0.1-0.2,0.2-0.2,0.3l-0.1,0.1c-0.1,0.1-0.1,0.1-0.2,0.1     c-0.1,0-0.1,0.1-0.2,0.1c-0.1,0-0.1,0.1-0.2,0.1c-0.1,0-0.1,0.1-0.2,0.1c-0.2,0.1-0.4,0.2-0.5,0.3c-0.1,0-0.1,0-0.2,0.1     c-0.1,0.1-0.3,0.1-0.5,0.2c-0.1,0-0.1,0-0.2,0.1c-0.1,0-0.2,0.1-0.3,0.1s-0.2,0-0.2,0.1c-0.1,0-0.2,0-0.3,0.1c-0.1,0-0.1,0-0.2,0     c-0.2,0-0.4,0.1-0.7,0.1c-0.1,0-0.1,0-0.2,0c-0.1,0-0.3,0-0.4,0c-0.1,0-0.2,0-0.3,0s-0.2,0-0.3,0s-0.2,0-0.3,0s-0.2,0-0.3,0     s-0.2,0-0.3,0s-0.2,0-0.3,0s-0.2,0-0.3,0s-0.2,0-0.3-0.1c-0.1,0-0.2,0-0.2,0c-0.1,0-0.2-0.1-0.4-0.1c-0.1,0-0.1,0-0.2-0.1     c-0.2-0.1-0.4-0.1-0.6-0.2c-0.1,0-0.2-0.1-0.3-0.1c0,0-0.1,0-0.2-0.1c-0.1-0.1-0.3-0.1-0.4-0.2c-1.1-0.7-1.7-1.5-1.7-2.4v1.4     c0,0.9,0.6,1.7,1.7,2.4c0.1,0.1,0.3,0.2,0.4,0.2c0,0,0.1,0,0.1,0.1c0.1,0,0.2,0.1,0.3,0.1l0,0c0.2,0.1,0.4,0.1,0.6,0.2l0,0h0.1     c0.1,0,0.2,0.1,0.4,0.1h0.1h0.1c0.1,0,0.2,0,0.3,0.1h0.1h0.1c0.1,0,0.2,0,0.3,0h0.1h0.1c0.1,0,0.2,0,0.3,0h0.1h0.1     c0.1,0,0.2,0,0.3,0s0.1,0,0.2,0h0.1c0.1,0,0.3,0,0.4,0c0.1,0,0.1,0,0.2,0l0,0c0.2,0,0.4-0.1,0.7-0.1l0,0c0.1,0,0.1,0,0.2,0     c0.1,0,0.2,0,0.3-0.1c0.1,0,0.2,0,0.2-0.1c0.1,0,0.2-0.1,0.3-0.1c0,0,0,0,0.1,0h0.1c0.2-0.1,0.3-0.1,0.5-0.2c0.1,0,0.1,0,0.2-0.1     c0.2-0.1,0.4-0.2,0.5-0.3c0,0,0,0,0.1,0s0.1-0.1,0.1-0.1c0.1,0,0.1-0.1,0.2-0.1c0.1,0,0.1-0.1,0.2-0.1c0.1,0,0.1-0.1,0.2-0.1l0,0     c0,0,0,0,0.1-0.1s0.2-0.2,0.2-0.3c0,0,0,0,0.1-0.1c0,0,0,0,0-0.1c0.1-0.1,0.1-0.1,0.1-0.2c0,0,0-0.1,0.1-0.1l0,0     c0-0.1,0.1-0.1,0.1-0.2V684l0,0c0-0.1,0-0.1,0-0.2v-0.1l0,0c0-0.1,0-0.1,0-0.2v-0.1c0,0,0,0,0-0.1l0,0L916.9,682z"
            />
            <g>
              <path
                class="st63"
                d="M915.2,679.6c2.3,1.3,2.3,3.4,0,4.8c-2.3,1.3-5.9,1.3-8.2,0s-2.3-3.4,0-4.8      C909.2,678.3,912.9,678.3,915.2,679.6z"
              />
            </g>
          </g>
        </g>
        <g>
          <g>
            <g>
              <g class="st61">
                <path
                  class="st62"
                  d="M902.5,666.4c0-0.1,0.1-0.2,0.1-0.2l0,0c0-0.1,0.1-0.1,0.1-0.2l0,0c0.1-0.1,0.1-0.1,0.2-0.1h0.1l0,0h0.1       l0,0h0.1l0,0c0,0,0,0,0.1,0l-0.5-0.3l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0c0,0,0,0-0.1,0       s-0.1,0.1-0.2,0.1l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0c0,0,0,0,0,0.1c0,0,0,0,0,0.1v0.1       c0,0.1,0.1,0.2,0.1,0.3l0.5,0.3c-0.1,0-0.1-0.2-0.1-0.3C902.4,666.5,902.4,666.4,902.5,666.4L902.5,666.4z"
                />
                <g>
                  <path
                    class="st64"
                    d="M902.9,665.8c0.3-0.1,0.5,0,0.5,0.3s-0.2,0.6-0.5,0.8c-0.3,0.1-0.5,0-0.5-0.3S902.6,665.9,902.9,665.8z"
                  />
                </g>
              </g>
            </g>
            <g>
              <g class="st61">
                <path
                  class="st62"
                  d="M903,667.9c0-0.1,0.1-0.3,0.1-0.4l0,0c0-0.2,0.1-0.3,0.2-0.5l0,0c0.1-0.2,0.1-0.3,0.2-0.4l0,0       c0.1-0.1,0.2-0.3,0.3-0.4l0,0c0.1-0.1,0.2-0.2,0.3-0.3c0,0,0,0,0.1-0.1s0.2-0.2,0.2-0.2l0.1-0.1c0.1-0.1,0.2-0.2,0.4-0.2       c0.1,0,0.2-0.1,0.2-0.1h0.1h0.1h0.1h0.1h0.1c0,0,0,0,0.1,0h0.1l0,0h0.1l0,0h0.1l0,0c0,0,0.1,0,0.1,0.1l-0.9-0.5l0,0h-0.1l0,0       l0,0l0,0c0,0,0,0-0.1,0l0,0l0,0h-0.1l0,0l0,0h-0.1l0,0c0,0,0,0-0.1,0c0,0,0,0-0.1,0l0,0h-0.1l0,0l0,0h-0.1l0,0l0,0       c-0.1,0-0.2,0.1-0.2,0.1c-0.1,0.1-0.2,0.2-0.4,0.2l-0.1,0.1c-0.1,0-0.1,0.1-0.2,0.1c0,0,0,0.1-0.1,0.1c0,0,0,0-0.1,0.1       c0,0,0,0.1-0.1,0.1c-0.1,0.1-0.1,0.1-0.2,0.2c0,0,0,0-0.1,0.1l0,0c0,0,0,0,0,0.1s-0.1,0.1-0.1,0.2c0,0.1-0.1,0.1-0.1,0.2l0,0       l0,0l-0.1,0.1c0,0.1-0.1,0.1-0.1,0.2c0,0,0,0.1-0.1,0.2l0,0l0,0c0,0.1,0,0.1-0.1,0.2c0,0.1,0,0.1-0.1,0.2v0.1l0,0       c0,0,0,0,0,0.1s0,0.1-0.1,0.2v0.1c0,0,0,0,0,0.1c0,0,0,0,0,0.1s0,0.3,0,0.4c0,0.5,0.1,0.8,0.3,1c0.1,0.1,0.1,0.1,0.2,0.2       l0.9,0.5c-0.3-0.2-0.6-0.6-0.6-1.2C902.9,668.3,902.9,668.1,903,667.9C903,668,903,667.9,903,667.9z"
                />
                <g>
                  <path
                    class="st35"
                    d="M904.8,665.1c1.1-0.6,1.9-0.1,1.9,1.1c0,1.2-0.9,2.7-1.9,3.3c-1.1,0.6-1.9,0.1-1.9-1.1        C902.9,667.2,903.8,665.7,904.8,665.1z"
                  />
                </g>
              </g>
            </g>
            <path
              class="st63"
              d="M909.1,667.1L909.1,667.1l-2.9-1.7l0,0h-0.1H906c0,0,0,0-0.1,0l0,0l0,0h-0.1h-0.1l0,0l0,0c0,0,0,0-0.1,0     h-0.1c0,0,0,0-0.1,0c0,0,0,0-0.1,0c0,0,0,0-0.1,0s-0.2,0.1-0.2,0.1c-0.1,0.1-0.2,0.1-0.3,0.2l-0.1,0.1l-0.1,0.1c0,0,0,0-0.1,0.1     c0,0,0,0-0.1,0.1c0,0,0,0-0.1,0.1c0,0.1-0.1,0.1-0.1,0.2c0,0,0,0,0,0.1l0,0c0,0,0,0,0,0.1l-0.1,0.1l-0.1,0.1l0,0l0,0     c0,0,0,0.1-0.1,0.1c0,0,0,0.1-0.1,0.1c0,0,0,0.1-0.1,0.1l0,0l0,0c0,0,0,0.1-0.1,0.1c0,0.1,0,0.1-0.1,0.2v0.1l0,0c0,0,0,0,0,0.1     s0,0.1,0,0.2v0.1c0,0,0,0,0,0.1c0,0,0,0,0,0.1s0,0.2,0,0.3c0,0.3,0,0.5,0.1,0.7s0.2,0.3,0.3,0.4l2.9,1.7l-0.1-0.1     c0.3,0.3,0.8,0.3,1.3,0c0.9-0.5,1.6-1.8,1.6-2.8C909.5,667.7,909.4,667.3,909.1,667.1z"
            />
          </g>
          <path
            class="st35"
            d="M915.3,680.1v-15.5c0-0.5,0-1,0-1.6v-1.4v-0.1v-0.1v-0.1v-0.1v-0.1V661c0,0,0-0.1-0.1-0.1c0,0,0-0.1-0.1-0.1    l-0.1-0.1c0,0,0-0.1-0.1-0.1l-0.1-0.1l-0.1-0.1c0,0-0.1-0.1-0.2-0.1c0,0-0.1,0-0.1-0.1c-0.1-0.1-0.2-0.1-0.3-0.2    c-0.1-0.1-0.2-0.1-0.3-0.2c0,0-0.1,0-0.1-0.1c-0.1,0-0.1-0.1-0.2-0.1l0,0l0,0c-0.1-0.1-0.3-0.1-0.4-0.1c0,0,0,0-0.1,0h-0.1    c-0.1,0-0.2,0-0.3-0.1h-0.1c0,0,0,0-0.1,0s-0.1,0-0.2,0h-0.1c0,0,0,0-0.1,0s-0.1,0-0.2,0h-0.1c0,0,0,0-0.1,0s-0.1,0-0.2,0h-0.1    c0,0,0,0-0.1,0s-0.2,0-0.2,0h-0.1l0,0c-0.1,0-0.3,0-0.4,0c0,0,0,0-0.1,0l0,0c-0.2,0-0.3,0-0.5,0.1c0,0,0,0-0.1,0h-0.1    c-0.1,0-0.1,0-0.2,0c-0.1,0-0.1,0-0.2,0c-0.1,0-0.1,0-0.2,0.1H909c0,0,0,0-0.1,0s-0.2,0.1-0.3,0.1c0,0-0.1,0-0.1,0.1    c-0.1,0.1-0.3,0.1-0.4,0.2c0,0-0.1,0-0.1,0.1c0,0-0.1,0-0.1,0.1c0,0-0.1,0-0.1,0.1l0,0l-0.1,0.1c0,0-0.1,0-0.1,0.1l0,0l0,0l0,0    l-0.1,0.1l-0.1,0.1l0,0l0,0l0,0l0,0c0,0,0,0.1-0.1,0.1l0,0c0,0,0,0,0,0.1l0,0l0,0v0.1l0,0v0.1l0,0l0,0v0.1l0,0v0.1l0,0l0,0v0.1    l0,0c0,0,0,0,0,0.1l0,0l0,0l0,0l-0.1,19.9v0.1v0.1v0.1v0.1v0.1v0.1c0,0,0,0.1,0.1,0.1c0,0,0,0.1,0.1,0.1l0.1,0.1    c0,0,0,0.1,0.1,0.1l0.1,0.1l0.1,0.1c0,0,0.1,0.1,0.2,0.1c0,0,0.1,0,0.1,0.1c0.1,0.1,0.2,0.1,0.3,0.2c0.1,0.1,0.2,0.1,0.3,0.2    c0,0,0.1,0,0.1,0.1c0.1,0,0.1,0.1,0.2,0.1l0,0l0,0c0.1,0.1,0.3,0.1,0.4,0.1c0,0,0,0,0.1,0h0.1c0.1,0,0.2,0,0.3,0.1h0.1    c0,0,0,0,0.1,0s0.2,0,0.2,0h0.1c0,0,0,0,0.1,0s0.1,0,0.2,0h0.1c0,0,0,0,0.1,0s0.1,0,0.2,0h0.1c0,0,0,0,0.1,0s0.2,0,0.2,0h0.1l0,0    c0.1,0,0.3,0,0.4,0c0,0,0,0,0.1,0l0,0c0.2,0,0.3,0,0.5-0.1c0,0,0,0,0.1,0h0.1c0.1,0,0.1,0,0.2,0c0.1,0,0.1,0,0.2,0    c0.1,0,0.1,0,0.2-0.1h0.1c0,0,0,0,0.1,0s0.2-0.1,0.3-0.1c0,0,0.1,0,0.1-0.1c0.1-0.1,0.3-0.1,0.4-0.2c0,0,0.1,0,0.1-0.1    c0,0,0.1,0,0.1-0.1c0,0,0.1,0,0.1-0.1l0,0l0.1-0.1c0,0,0.1,0,0.1-0.1l0,0l0,0l0,0l0.1-0.1l0.1-0.1l0,0l0,0l0,0l0,0    c0,0,0-0.1,0.1-0.1l0,0c0,0,0,0,0-0.1l0,0l0,0V682l0,0v-0.1l0,0l0,0v-0.1l0,0v-0.1l0,0l0,0v-0.1l0,0c0,0,0,0,0-0.1l0,0l0,0l0,0    C915.3,681.1,915.3,680.6,915.3,680.1z"
          />
          <g>
            <path
              class="st63"
              d="M918.2,671.2L918.2,671.2l-2.9-1.7l0,0h-0.1h-0.1c0,0,0,0-0.1,0l0,0l0,0h-0.1h-0.1l0,0l0,0c0,0,0,0-0.1,0     h-0.1c0,0,0,0-0.1,0c0,0,0,0-0.1,0c0,0,0,0-0.1,0s-0.2,0.1-0.2,0.1c-0.1,0.1-0.2,0.1-0.3,0.2l-0.1,0.1l-0.1,0.1c0,0,0,0-0.1,0.1     c0,0,0,0-0.1,0.1c0,0,0,0-0.1,0.1c0,0.1-0.1,0.1-0.1,0.2c0,0,0,0,0,0.1l0,0c0,0,0,0,0,0.1l-0.1,0.1l-0.1,0.1l0,0l0,0     c0,0,0,0.1-0.1,0.1c0,0,0,0.1-0.1,0.1c0,0,0,0.1-0.1,0.1l0,0l0,0c0,0,0,0.1-0.1,0.1c0,0.1,0,0.1-0.1,0.2v0.1l0,0c0,0,0,0,0,0.1     s0,0.1,0,0.2v0.1c0,0,0,0,0,0.1c0,0,0,0,0,0.1s0,0.2,0,0.3c0,0.4,0.1,0.7,0.3,0.9c0.1,0.1,0.1,0.1,0.2,0.2l2.9,1.7l-0.1-0.1     c0.3,0.3,0.8,0.3,1.3,0c0.9-0.5,1.6-1.8,1.6-2.8C918.6,671.7,918.4,671.4,918.2,671.2z"
            />
            <g>
              <g class="st61">
                <path
                  class="st62"
                  d="M915.8,674.2c0-0.1,0.1-0.3,0.1-0.4l0,0c0-0.2,0.1-0.3,0.2-0.5l0,0c0.1-0.2,0.1-0.3,0.2-0.4l0,0       c0.1-0.1,0.2-0.3,0.3-0.4l0,0c0.1-0.1,0.2-0.2,0.3-0.3c0,0,0,0,0.1-0.1s0.2-0.2,0.2-0.2l0.1-0.1c0.1-0.1,0.2-0.2,0.4-0.2       c0.1,0,0.2-0.1,0.2-0.1h0.1h0.1h0.1h0.1h0.1c0,0,0,0,0.1,0h0.1l0,0h0.1l0,0h0.1l0,0c0,0,0.1,0,0.1,0.1l-0.9-0.5l0,0h-0.1l0,0       l0,0l0,0c0,0,0,0-0.1,0l0,0l0,0h-0.1l0,0l0,0h-0.1l0,0c0,0,0,0-0.1,0c0,0,0,0-0.1,0l0,0h-0.1l0,0l0,0h-0.1l0,0l0,0       c-0.1,0-0.2,0.1-0.2,0.1c-0.1,0.1-0.2,0.2-0.4,0.2l-0.1,0.1c-0.1,0-0.1,0.1-0.2,0.1c0,0,0,0.1-0.1,0.1c0,0,0,0-0.1,0.1       c0,0,0,0.1-0.1,0.1c-0.1,0.1-0.1,0.1-0.2,0.2c0,0,0,0-0.1,0.1l0,0c0,0,0,0,0,0.1s-0.1,0.1-0.1,0.2c0,0.1-0.1,0.1-0.1,0.2l0,0       l0,0l-0.1,0.1c0,0.1-0.1,0.1-0.1,0.2c0,0.1,0,0.1-0.1,0.2l0,0l0,0c0,0.1,0,0.1-0.1,0.2c0,0.1,0,0.1-0.1,0.2v0.1l0,0       c0,0,0,0,0,0.1s0,0.1-0.1,0.2v0.1c0,0,0,0,0,0.1c0,0,0,0,0,0.1s0,0.3,0,0.4c0,0.6,0.2,1,0.6,1.2l0.9,0.5       c-0.3-0.2-0.6-0.6-0.6-1.2C915.8,674.6,915.8,674.5,915.8,674.2C915.8,674.3,915.8,674.3,915.8,674.2z"
                />
                <g>
                  <path
                    class="st63"
                    d="M917.7,671.4c1.1-0.6,1.9-0.1,1.9,1.1c0,1.2-0.9,2.7-1.9,3.3c-1.1,0.6-1.9,0.1-1.9-1.1        C915.8,673.5,916.6,672,917.7,671.4z"
                  />
                </g>
              </g>
            </g>
            <g>
              <g class="st61">
                <path
                  class="st62"
                  d="M917.9,673.9c0-0.1,0.1-0.2,0.1-0.2l0,0c0-0.1,0.1-0.1,0.1-0.2l0,0c0.1-0.1,0.1-0.1,0.2-0.1h0.1l0,0h0.1       l0,0h0.1l0,0c0,0,0,0,0.1,0l-0.5-0.3l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0c0,0,0,0-0.1,0       s-0.1,0.1-0.2,0.1l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0c0,0,0,0,0,0.1c0,0,0,0,0,0.1v0.1       c0,0.1,0.1,0.2,0.1,0.3l0.5,0.3c-0.1,0-0.1-0.2-0.1-0.3C917.8,674.1,917.9,674,917.9,673.9L917.9,673.9z"
                />
                <g>
                  <path
                    class="st35"
                    d="M918.3,673.4c0.3-0.1,0.5,0,0.5,0.3s-0.2,0.6-0.5,0.8c-0.3,0.1-0.5,0-0.5-0.3S918,673.5,918.3,673.4z"
                  />
                </g>
              </g>
            </g>
          </g>
        </g>
        <g>
          <g>
            <g class="st61">
              <path
                class="st62"
                d="M916,660.9c0,0.1,0,0.1,0,0.2v0.1v0.1c0,0,0,0.1-0.1,0.1c0,0.1,0,0.1-0.1,0.2c0,0,0,0.1-0.1,0.1      c0,0.1-0.1,0.1-0.1,0.2l-0.1,0.1c-0.1,0.1-0.1,0.2-0.2,0.2l-0.1,0.1c0,0-0.1,0.1-0.2,0.1l-0.1,0.1c-0.1,0-0.1,0.1-0.2,0.1      c-0.1,0-0.1,0.1-0.2,0.1c-0.1,0.1-0.3,0.2-0.5,0.2c0,0-0.1,0-0.1,0.1c-0.1,0.1-0.3,0.1-0.4,0.1h-0.1c-0.1,0-0.2,0-0.3,0.1      c-0.1,0-0.1,0-0.2,0.1c-0.1,0-0.2,0-0.3,0.1c-0.1,0-0.1,0-0.2,0c-0.2,0-0.4,0.1-0.6,0.1c-0.1,0-0.1,0-0.2,0c-0.1,0-0.2,0-0.3,0      s-0.1,0-0.2,0c-0.1,0-0.2,0-0.3,0s-0.2,0-0.2,0c-0.1,0-0.2,0-0.2,0c-0.1,0-0.2,0-0.2,0c-0.1,0-0.2,0-0.2,0c-0.1,0-0.1,0-0.2,0      c-0.1,0-0.2,0-0.3,0s-0.1,0-0.2,0c-0.1,0-0.2,0-0.3-0.1c-0.1,0-0.1,0-0.2,0c-0.2-0.1-0.4-0.1-0.5-0.2c-0.1,0-0.2-0.1-0.2-0.1      s-0.1,0-0.1-0.1c-0.1-0.1-0.2-0.1-0.4-0.2c-1-0.6-1.5-1.3-1.5-2v2.7c0,0.7,0.5,1.5,1.5,2c0.1,0.1,0.2,0.1,0.4,0.2      c0,0,0.1,0,0.1,0.1c0.1,0,0.1,0.1,0.2,0.1l0,0c0.2,0.1,0.3,0.1,0.5,0.2l0,0h0.1c0.1,0,0.2,0.1,0.3,0.1h0.1h0.1      c0.1,0,0.2,0,0.3,0h0.1h0.1c0.1,0,0.2,0,0.2,0h0.1h0.1c0.1,0,0.2,0,0.2,0h0.1h0.1c0.1,0,0.2,0,0.3,0h0.1h0.1c0.1,0,0.2,0,0.3,0      h0.1l0,0c0.2,0,0.4,0,0.6-0.1l0,0c0.1,0,0.1,0,0.2,0c0.1,0,0.2,0,0.3-0.1c0.1,0,0.1,0,0.2-0.1c0.1,0,0.2,0,0.3-0.1      c0,0,0,0,0.1,0h0.1c0.1,0,0.3-0.1,0.4-0.1c0,0,0.1,0,0.1-0.1c0.2-0.1,0.3-0.1,0.5-0.2c0,0,0,0,0.1,0l0.1-0.1      c0.1,0,0.1-0.1,0.2-0.1l0.1-0.1c0.1,0,0.1-0.1,0.2-0.1l0,0c0,0,0,0,0.1-0.1s0.1-0.1,0.2-0.2l0,0c0,0,0,0,0-0.1s0.1-0.1,0.1-0.2      c0,0,0,0,0-0.1l0,0c0-0.1,0-0.1,0.1-0.2V664l0,0v-0.1v-0.1l0,0c0-0.1,0-0.1,0-0.2v-0.1l0,0v-2.7C916,660.8,916,660.8,916,660.9z      "
              />
              <g>
                <path
                  class="st63"
                  d="M914.6,658.7c1.9,1.1,1.9,2.9,0,4s-5,1.1-7,0c-1.9-1.1-1.9-2.9,0-4C909.5,657.6,912.6,657.6,914.6,658.7z       "
                />
              </g>
            </g>
          </g>
          <g>
            <path
              class="st35"
              d="M915.4,660.3L915.4,660.3c0-0.1,0-0.2,0-0.3C915.4,660.1,915.4,660.1,915.4,660.3v-0.2c0-0.2,0-0.3,0-0.5     l0,0l0,0c0,0,0,0,0-0.1s0-0.2,0-0.3l0,0l0,0c0-0.1,0-0.1,0-0.2c0,0,0,0,0-0.1v-0.1v-0.1l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0     c0-0.1,0-0.2,0-0.2c0-0.1,0-0.2-0.1-0.3c0,0,0,0,0-0.1s-0.1-0.3-0.1-0.4l0,0l0,0l0,0c0-0.1-0.1-0.2-0.1-0.3v-0.1v0.1l0,0v-0.1     c0,0,0,0,0-0.1v-0.1V657c0-0.1,0-0.1-0.1-0.1c0,0,0-0.1-0.1-0.1l0,0l0,0l0,0c0,0,0-0.1-0.1-0.1c0-0.1-0.1-0.1-0.1-0.2     c-0.5-0.8-1.2-1.4-1.9-1.6l0,0c0,0-0.1,0-0.2-0.1l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0c-0.1,0-0.1,0-0.2-0.1l0,0l0,0h-0.1l0,0     l0,0l0,0l0,0c0,0,0,0-0.1,0h-0.1l0,0H912l0,0l0,0l0,0l0,0l0,0h-0.2c0,0-0.1,0-0.2,0h-0.1l0,0c0,0,0,0-0.1,0l0,0l0,0l0,0h-0.1     h-0.1l0,0l0,0H911l0,0l0,0h-0.1l0,0c0,0,0,0,0.1,0c0,0,0,0-0.1,0c0,0,0,0-0.1,0l0,0c0,0-0.1,0-0.2,0l0,0l0,0l0,0l0,0l0,0     c0,0,0,0-0.1,0h-0.1c0,0-0.1,0-0.2,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0h-0.1c-0.1,0-0.1,0.1-0.2,0.1l0,0     c-0.1,0-0.1,0.1-0.2,0.1l0,0l0,0l0,0c0,0-0.1,0.1-0.2,0.1h-0.1l0,0l0,0h-0.1l0,0l0,0l0,0l0,0l0,0l0,0l-0.1,0.1l0,0l-0.1,0.1l0,0     l0,0c0,0,0,0-0.1,0l0,0l-0.1,0.1l-0.1,0.1c-0.1,0.1-0.1,0.1-0.2,0.2l0,0c0,0,0,0,0,0.1l0,0l0,0l0,0c-0.1,0.1-0.1,0.2-0.2,0.2     c0,0,0,0,0,0.1c0,0,0,0.1-0.1,0.1l-0.1,0.1l0,0c0,0,0,0.1-0.1,0.1l0,0l0,0l0,0c0,0,0,0.1-0.1,0.1l0,0l0,0l0,0l0,0l0,0     c0,0,0,0,0,0.1s-0.1,0.2-0.1,0.2l0,0l0,0c0,0,0,0,0,0.1l0,0l0,0v0.1c0,0.1-0.1,0.2-0.1,0.3l0,0c-0.1,0.2-0.1,0.4-0.1,0.5l0,0v0.1     c0,0.1,0,0.3-0.1,0.4l0,0v0.1l0,0c0,0.2-0.1,0.4-0.1,0.6c0,0,0,0,0,0.1c0,0.2,0,0.4,0,0.7l0,0l0,0l0,0l0,0v0.1v0.1v0.1v0.1     c0,0,0,0.1,0.1,0.1v0.1c0,0,0,0.1,0.1,0.1c0,0,0,0.1,0.1,0.1l0.1,0.1c0,0,0,0.1,0.1,0.1l0.1,0.1l0.1,0.1c0.1,0,0.1,0.1,0.2,0.1     c0,0,0.1,0,0.1,0.1c0.1,0.1,0.2,0.1,0.3,0.2c0.1,0.1,0.2,0.1,0.4,0.2l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0     c0,0,0,0,0.1,0c0,0,0,0,0.1,0h0.1c0,0,0,0,0.1,0c0,0,0,0,0.1,0h0.1l0,0l0,0l0,0l0,0l0,0c0,0,0,0,0.1,0h0.1l0,0c0,0,0,0,0.1,0     c0,0,0,0,0.1,0l0,0c0,0,0,0,0.1,0l0,0l0,0c0,0,0,0,0.1,0h0.1l0,0c0,0,0,0,0.1,0l0,0c0.1,0,0.1,0,0.2,0l0,0l0,0l0,0l0,0     c0,0,0,0,0.1,0c0,0,0,0,0.1,0h0.1h0.1l0,0l0,0c0,0,0,0,0.1,0l0,0h0.1h0.1c0,0,0,0,0.1,0l0,0l0,0c0,0,0,0,0.1,0h0.1l0,0h0.1     c0,0,0,0,0.1,0c0,0,0,0,0.1,0l0,0l0,0c0.1,0,0.2,0,0.4,0h0.1l0,0l0,0l0,0l0,0c0.1,0,0.1,0,0.2,0c0.1,0,0.2,0,0.4,0l0,0     c0.1,0,0.2,0,0.2,0c0.1,0,0.1,0,0.2-0.1c0.1,0,0.3-0.1,0.4-0.1l0,0c0.3-0.1,0.6-0.2,0.9-0.4c0,0,0.1,0,0.1-0.1l0,0     c0,0,0.1,0,0.1-0.1c0,0,0.1,0,0.1-0.1l0,0l0.1-0.1C915.3,660.5,915.4,660.5,915.4,660.3L915.4,660.3     C915.4,660.4,915.4,660.4,915.4,660.3L915.4,660.3L915.4,660.3L915.4,660.3h0.2l0,0l0.1-0.1l0,0l0,0l0,0l0,0l0,0l0,0     c0,0,0-0.1,0.1-0.1c0,0,0,0,0-0.1l0,0l0,0l0,0l0,0l0,0l0,0l0,0v-0.1l0,0l0,0l0,0l0,0l0,0l0,0l0,0c0,0,0,0,0-0.1l0,0l0,0l0,0l0,0     l0,0l0,0l0,0l0,0l0,0C915.4,660.5,915.4,660.4,915.4,660.3C915.4,660.4,915.4,660.4,915.4,660.3L915.4,660.3L915.4,660.3     L915.4,660.3z M915.4,660.3L915.4,660.3L915.4,660.3L915.4,660.3z"
            />
            <path class="st65" d="M911.7,654.6L911.7,654.6L911.7,654.6z" />
          </g>
          <g>
            <g class="st61">
              <path
                class="st62"
                d="M912.3,655.1C912.3,655.2,912.3,655.2,912.3,655.1C912.3,655.2,912.3,655.2,912.3,655.1L912.3,655.1      l-0.2,0.2c0,0-0.1,0-0.1,0.1c0,0,0,0-0.1,0.1c-0.1,0-0.2,0.1-0.3,0.1h-0.1h-0.1c-0.1,0-0.1,0-0.2,0l0,0c-0.1,0-0.1,0-0.2,0l0,0      c-0.1,0-0.1,0-0.2,0l0,0c-0.1,0-0.1,0-0.2,0l0,0c-0.1,0-0.1,0-0.2-0.1l0,0c-0.1,0-0.1-0.1-0.2-0.1c-0.3-0.1-0.4-0.3-0.4-0.5v0.3      c0,0.2,0.1,0.4,0.4,0.5c0.1,0,0.1,0.1,0.2,0.1l0,0l0,0h0.1c0,0,0,0,0.1,0l0,0l0,0h0.1l0,0l0,0l0,0h0.1l0,0l0,0c0,0,0,0,0.1,0      h0.1l0,0l0,0h0.1l0,0h0.1c0,0,0,0,0.1,0l0,0c0.1,0,0.2-0.1,0.3-0.1l0,0c0,0,0,0,0.1,0c0,0,0.1,0,0.1-0.1l0,0l0.1-0.1l0,0l0,0      l0,0c0,0,0,0,0-0.1l0,0l0,0l0,0c0,0,0,0,0-0.1l0,0V655C912.4,655.1,912.4,655.1,912.3,655.1z"
              />
              <g>
                <path
                  class="st63"
                  d="M912,654.5c0.5,0.3,0.5,0.8,0,1c-0.5,0.3-1.3,0.3-1.8,0s-0.5-0.8,0-1C910.7,654.2,911.5,654.2,912,654.5z       "
                />
              </g>
            </g>
          </g>
          <path
            class="st35"
            d="M911.8,653.7L911.8,653.7L911.8,653.7C911.8,653.8,911.8,653.8,911.8,653.7c-0.1,0.1-0.1,0.1-0.1,0.2    c0,0,0,0-0.1,0l0,0c-0.1,0-0.1,0.1-0.2,0.1l0,0c-0.1,0-0.2,0-0.3,0l0,0c-0.1,0-0.2,0-0.3,0l0,0c-0.1,0-0.2-0.1-0.2-0.1    c-0.1-0.1-0.2-0.2-0.2-0.3v1.4c0,0.1,0.1,0.2,0.2,0.3c0,0,0.1,0,0.1,0.1h0.1l0,0l0,0l0,0c0,0,0,0,0.1,0c0,0,0,0,0.1,0    c0,0,0,0,0.1,0l0,0l0,0l0,0h0.1h0.1l0,0l0,0c0,0,0,0,0.1,0s0.1,0,0.2-0.1l0,0l0,0c0,0,0,0,0.1,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0l0,0    l0,0l0,0l0,0L911.8,653.7C911.8,653.6,911.8,653.7,911.8,653.7z"
          />
          <g>
            <path
              class="st62"
              d="M911.6,653.3c0.3,0.2,0.3,0.4,0,0.6s-0.8,0.2-1,0c-0.3-0.2-0.3-0.4,0-0.6     C910.8,653.1,911.3,653.1,911.6,653.3z"
            />
          </g>
        </g>
      </g>
      <g>
        <g>
          <polygon
            class="st2"
            points="600.5,494.2 582,504.9 622.4,527.3 640.8,516.7    "
          />
        </g>
      </g>
      <g>
        <g>
          <polygon
            class="st2"
            points="1364,495 1019.9,690.2 1036.9,700.6 1381,505.5    "
          />
        </g>
      </g>
      <g>
        <g>
          <polygon
            class="st2"
            points="1364,495 1019.9,690.2 1036.9,700.6 1381,505.5    "
          />
        </g>
      </g>
      <g>
        <polygon
          class="st35"
          points="1209.6,585 1221.9,593.2 1239.3,583.3 1226.7,575.4 "
        />
        <ellipse class="st7" cx="1228.2" cy="587.1" rx="1.2" ry="0.5" />
      </g>
      <g>
        <circle class="st22" cx="1047.4" cy="524.3" r="3.6" />
        <circle class="st22" cx="1053.7" cy="526.4" r="3.6" />
      </g>
      <g>
        <circle class="st22" cx="1348.1" cy="360" r="3.6" />
        <circle class="st22" cx="1354.4" cy="362" r="3.6" />
      </g>
      <g>
        <path
          class="st24"
          d="M1365.6,512c-3.2,1.6-8,1.5-10.6-0.3c-0.7-0.5-1.2-1-1.4-1.6c-0.7-1.5,0.3-3.3,2.6-4.4    c3.2-1.6,8-1.5,10.6,0.3S1368.9,510.4,1365.6,512z"
        />
        <polygon
          class="st25"
          points="1360.9,513.1 1359,513 1356.9,512.6 1355.6,512.1 1354.5,511.3 1353.6,510.1 1353.4,508.8     1353.4,325.4 1361.3,325.4   "
        />
        <rect x="1360.9" y="325.4" class="st24" width="7.5" height="183.4" />

        <ellipse
          transform="matrix(0.9986 -5.345226e-02 5.345226e-02 0.9986 -15.4484 73.2105)"
          class="st25"
          cx="1360.9"
          cy="325.4"
          rx="7.5"
          ry="4.3"
        />
      </g>
      <g>
        <circle class="st22" cx="1367.4" cy="366.9" r="3.6" />
        <circle class="st22" cx="1373.8" cy="369" r="3.6" />
      </g>
      <path class="st26" d="M1051.1,526.4c0,0,145.1-30.6,300.7-164.4" />
      <g>
        <path
          class="st24"
          d="M1066.7,680.1c-3.2,1.6-8,1.5-10.6-0.3c-0.7-0.5-1.2-1-1.4-1.6c-0.7-1.5,0.3-3.3,2.6-4.4    c3.2-1.6,8-1.5,10.6,0.3C1070.4,675.8,1069.9,678.5,1066.7,680.1z"
        />
        <polygon
          class="st25"
          points="1062,681.2 1060,681.2 1057.9,680.7 1056.7,680.2 1055.5,679.4 1054.6,678.2 1054.5,677     1054.5,493.6 1062.3,493.6   "
        />
        <rect x="1062" y="493.6" class="st24" width="7.5" height="183.4" />

        <ellipse
          transform="matrix(0.9986 -5.345226e-02 5.345226e-02 0.9986 -24.8666 57.4736)"
          class="st25"
          cx="1062"
          cy="493.6"
          rx="7.5"
          ry="4.3"
        />
      </g>
      <g>
        <circle class="st22" cx="1066.7" cy="531.3" r="3.6" />
        <circle class="st22" cx="1073.1" cy="533.3" r="3.6" />
      </g>
      <path class="st26" d="M1069.2,532.3c0,0,145.1-30.6,300.7-164.4" />
      <g>
        <g>
          <polygon
            class="st27"
            points="1183.8,599.5 1073.5,662.1 1086.2,671.2 1198,607.7    "
          />
        </g>
      </g>
      <polyline
        class="st8"
        points="1184.7,600.2 1075.4,662.1 1077.3,663.7 1185.2,602.7  "
      />
      <g>
        <g>
          <g>
            <defs>
              <polygon
                id="SVGID_1_"
                points="1082.9,671.1 1242,580.9 1227.2,563.6 1065,655.6      "
              />
            </defs>
            <clipPath
              id="SVGID_00000132046094968397407350000006662601757973419179_"
            >
              <use
                xmlns:xlink="http://www.w3.org/1999/xlink"
                xlink:href="#SVGID_1_"
                style="overflow: visible"
              />
            </clipPath>
            <g
              style="
                clip-path: url(#SVGID_00000132046094968397407350000006662601757973419179_);
              "
            >
              <g>
                <path
                  class="st29"
                  d="M1089.6,665.4c-1-1.4-2.1-4.8,0-6.8c-2.1,0.8-3.2,2.4-3.4,4.6c-1.3-2.1-1.8-8.1,0-10        c-4.3,3.1-4,9.9-3.3,12.2H1089.6z"
                />
                <path
                  class="st30"
                  d="M1090.6,666.5c0.5-1.9,0.8-7.2-2.6-9.7c1.4,1.6,0,6.3-1,7.9c-0.1-1.7-1-3-2.7-3.6c1.6,1.6,0.8,4.2,0,5.4        L1090.6,666.5L1090.6,666.5z"
                />
              </g>
              <g>
                <path
                  class="st29"
                  d="M1103.9,654c-1-1.4-2.1-4.8,0-6.8c-2.1,0.8-3.2,2.4-3.4,4.6c-1.3-2.1-1.8-8.1,0-10        c-4.3,3.1-4,9.9-3.3,12.2H1103.9z"
                />
                <path
                  class="st30"
                  d="M1104.9,655c0.5-1.9,0.8-7.2-2.6-9.7c1.4,1.6,0,6.3-1,7.9c-0.1-1.7-1-3-2.7-3.6c1.6,1.6,0.8,4.2,0,5.4        L1104.9,655L1104.9,655z"
                />
              </g>
              <g>
                <path
                  class="st29"
                  d="M1127.1,641.7c-1-1.4-2.1-4.8,0-6.8c-2.1,0.8-3.2,2.4-3.4,4.6c-1.3-2.1-1.8-8.1,0-10        c-4.3,3.1-4,9.9-3.3,12.2H1127.1z"
                />
                <path
                  class="st30"
                  d="M1128.1,642.7c0.5-1.9,0.8-7.2-2.6-9.7c1.4,1.6,0,6.3-1,7.9c-0.1-1.7-1-3-2.7-3.6c1.6,1.6,0.8,4.2,0,5.4        L1128.1,642.7L1128.1,642.7z"
                />
              </g>
              <g>
                <path
                  class="st29"
                  d="M1140.3,632.1c-1-1.4-2.1-4.8,0-6.8c-2.1,0.8-3.2,2.4-3.4,4.6c-1.3-2.1-1.8-8.1,0-10        c-4.3,3.1-4,9.9-3.3,12.2H1140.3z"
                />
                <path
                  class="st30"
                  d="M1141.3,633.1c0.5-1.9,0.8-7.2-2.6-9.7c1.4,1.6,0,6.3-1,7.9c-0.1-1.7-1-3-2.7-3.6c1.6,1.6,0.8,4.2,0,5.4        L1141.3,633.1L1141.3,633.1z"
                />
              </g>
              <g>
                <path
                  class="st29"
                  d="M1163.4,627c-1-1.4-2.1-4.8,0-6.8c-2.1,0.8-3.2,2.4-3.4,4.6c-1.3-2.1-1.8-8.1,0-10        c-4.3,3.1-4,9.9-3.3,12.2H1163.4z"
                />
                <path
                  class="st30"
                  d="M1164.4,628c0.5-1.9,0.8-7.2-2.6-9.7c1.4,1.6,0,6.3-1,7.9c-0.1-1.7-1-3-2.7-3.6c1.6,1.6,0.8,4.2,0,5.4        L1164.4,628L1164.4,628z"
                />
              </g>
              <g>
                <path
                  class="st29"
                  d="M1185.2,606.5c-1-1.4-2.1-4.8,0-6.8c-2.1,0.8-3.2,2.4-3.4,4.6c-1.3-2.1-1.8-8.1,0-10        c-4.3,3.1-4,9.9-3.3,12.2H1185.2z"
                />
                <path
                  class="st30"
                  d="M1186.1,607.5c0.5-1.9,0.8-7.2-2.6-9.7c1.4,1.6,0,6.3-1,7.9c-0.1-1.7-1-3-2.7-3.6c1.6,1.6,0.8,4.2,0,5.4        L1186.1,607.5L1186.1,607.5z"
                />
              </g>
              <g>
                <path
                  class="st29"
                  d="M1177.1,614.7c-1-1.4-2.1-4.8,0-6.8c-2.1,0.8-3.2,2.4-3.4,4.6c-1.3-2.1-1.8-8.1,0-10        c-4.3,3.1-4,9.9-3.3,12.2H1177.1z"
                />
                <path
                  class="st30"
                  d="M1178.1,615.7c0.5-1.9,0.8-7.2-2.6-9.7c1.4,1.6,0,6.3-1,7.9c-0.1-1.7-1-3-2.7-3.6c1.6,1.6,0.8,4.2,0,5.4        L1178.1,615.7L1178.1,615.7z"
                />
              </g>
              <g>
                <path
                  class="st29"
                  d="M1095.3,659.8c-0.1-2.8-2.8-4-0.8-8.4c-2.7,3-2.3,4.4-2.1,6.8c-0.4-1.1-0.9-1.9-2-2.8        c0.5,1.4,0.3,2.8-0.4,4.4H1095.3L1095.3,659.8z"
                />
                <path
                  class="st30"
                  d="M1093.7,660.2c0-2-2.2-2.8-0.6-6c-2.1,2.1-1.7,3.1-1.6,4.9c-0.3-0.8-0.7-1.3-1.6-2c0.4,1,0.2,2-0.3,3.1        H1093.7L1093.7,660.2z"
                />
              </g>
              <g>
                <path
                  class="st29"
                  d="M1102.2,660.3c-0.1-2.8-2.8-4-0.8-8.4c-2.7,3-2.3,4.4-2.1,6.8c-0.4-1.1-0.9-1.9-2-2.8        c0.5,1.4,0.3,2.8-0.4,4.4H1102.2L1102.2,660.3z"
                />
                <path
                  class="st30"
                  d="M1100.7,660.7c0-2-2.2-2.8-0.6-6c-2.1,2.1-1.7,3.1-1.6,4.9c-0.3-0.8-0.7-1.3-1.6-2c0.4,1,0.2,2-0.3,3.1        H1100.7L1100.7,660.7z"
                />
              </g>
              <g>
                <path
                  class="st29"
                  d="M1114,653.3c-0.1-2.8-2.8-4-0.8-8.4c-2.7,3-2.3,4.4-2.1,6.8c-0.4-1.1-0.9-1.9-2-2.8        c0.5,1.4,0.3,2.8-0.4,4.4H1114L1114,653.3z"
                />
                <path
                  class="st30"
                  d="M1112.4,653.6c0-2-2.2-2.8-0.6-6c-2.1,2.1-1.7,3.1-1.6,4.9c-0.3-0.8-0.7-1.3-1.6-2c0.4,1,0.2,2-0.3,3.1        H1112.4L1112.4,653.6z"
                />
              </g>
              <g>
                <path
                  class="st29"
                  d="M1120.5,646.2c-0.1-2.8-2.8-4-0.8-8.4c-2.7,3-2.3,4.4-2.1,6.8c-0.4-1.1-0.9-1.9-2-2.8        c0.5,1.4,0.3,2.8-0.4,4.4H1120.5L1120.5,646.2z"
                />
                <path
                  class="st30"
                  d="M1118.9,646.6c0-2-2.2-2.8-0.6-6c-2.1,2.1-1.7,3.1-1.6,4.9c-0.3-0.8-0.7-1.3-1.6-2c0.4,1,0.2,2-0.3,3.1        H1118.9L1118.9,646.6z"
                />
              </g>
              <g>
                <path
                  class="st29"
                  d="M1120.6,650.1c-0.1-2.8-2.8-4-0.8-8.4c-2.7,3-2.3,4.4-2.1,6.8c-0.4-1.1-0.9-1.9-2-2.8        c0.5,1.4,0.3,2.8-0.4,4.4H1120.6L1120.6,650.1z"
                />
                <path
                  class="st30"
                  d="M1119,650.4c0-2-2.2-2.8-0.6-6c-2.1,2.1-1.7,3.1-1.6,4.9c-0.3-0.8-0.7-1.3-1.6-2c0.4,1,0.2,2-0.3,3.1        H1119L1119,650.4z"
                />
              </g>
              <g>
                <path
                  class="st29"
                  d="M1134,636.1c-0.1-2.8-2.8-4-0.8-8.4c-2.7,3-2.3,4.4-2.1,6.8c-0.4-1.1-0.9-1.9-2-2.8        c0.5,1.4,0.3,2.8-0.4,4.4H1134L1134,636.1z"
                />
                <path
                  class="st30"
                  d="M1132.5,636.4c0-2-2.2-2.8-0.6-6c-2.1,2.1-1.7,3.1-1.6,4.9c-0.3-0.8-0.7-1.3-1.6-2c0.4,1,0.2,2-0.3,3.1        H1132.5L1132.5,636.4z"
                />
              </g>
              <g>
                <path
                  class="st29"
                  d="M1143.9,636.5c-0.1-2.8-2.8-4-0.8-8.4c-2.7,3-2.3,4.4-2.1,6.8c-0.4-1.1-0.9-1.9-2-2.8        c0.5,1.4,0.3,2.8-0.4,4.4H1143.9L1143.9,636.5z"
                />
                <path
                  class="st30"
                  d="M1142.3,636.9c0-2-2.2-2.8-0.6-6c-2.1,2.1-1.7,3.1-1.6,4.9c-0.3-0.8-0.7-1.3-1.6-2c0.4,1,0.2,2-0.3,3.1        H1142.3L1142.3,636.9z"
                />
              </g>
              <g>
                <path
                  class="st29"
                  d="M1135.9,642.4c-0.1-2.8-2.8-4-0.8-8.4c-2.7,3-2.3,4.4-2.1,6.8c-0.4-1.1-0.9-1.9-2-2.8        c0.5,1.4,0.3,2.8-0.4,4.4H1135.9L1135.9,642.4z"
                />
                <path
                  class="st30"
                  d="M1134.4,642.8c0-2-2.2-2.8-0.6-6c-2.1,2.1-1.7,3.1-1.6,4.9c-0.3-0.8-0.7-1.3-1.6-2c0.4,1,0.2,2-0.3,3.1        H1134.4L1134.4,642.8z"
                />
              </g>
              <g>
                <path
                  class="st29"
                  d="M1154.8,623.1c-0.1-2.8-2.8-4-0.8-8.4c-2.7,3-2.3,4.4-2.1,6.8c-0.4-1.1-0.9-1.9-2-2.8        c0.5,1.4,0.3,2.8-0.4,4.4H1154.8L1154.8,623.1z"
                />
                <path
                  class="st30"
                  d="M1153.3,623.4c0-2-2.2-2.8-0.6-6c-2.1,2.1-1.7,3.1-1.6,4.9c-0.3-0.8-0.7-1.3-1.6-2c0.4,1,0.2,2-0.3,3.1        H1153.3L1153.3,623.4z"
                />
              </g>
              <g>
                <path
                  class="st29"
                  d="M1189,612.6c-0.1-2.8-2.8-4-0.8-8.4c-2.7,3-2.3,4.4-2.1,6.8c-0.4-1.1-0.9-1.9-2-2.8        c0.5,1.4,0.3,2.8-0.4,4.4H1189L1189,612.6z"
                />
                <path
                  class="st30"
                  d="M1187.5,613c0-2-2.2-2.8-0.6-6c-2.1,2.1-1.7,3.1-1.6,4.9c-0.3-0.8-0.7-1.3-1.6-2c0.4,1,0.2,2-0.3,3.1        H1187.5L1187.5,613z"
                />
              </g>
              <g>
                <path
                  class="st29"
                  d="M1175.7,621.7c-0.1-2.8-2.8-4-0.8-8.4c-2.7,3-2.3,4.4-2.1,6.8c-0.4-1.1-0.9-1.9-2-2.8        c0.5,1.4,0.3,2.8-0.4,4.4H1175.7L1175.7,621.7z"
                />
                <path
                  class="st30"
                  d="M1174.1,622c0-2-2.2-2.8-0.6-6c-2.1,2.1-1.7,3.1-1.6,4.9c-0.3-0.8-0.7-1.3-1.6-2c0.4,1,0.2,2-0.3,3.1        H1174.1L1174.1,622z"
                />
              </g>
              <g>
                <path
                  class="st29"
                  d="M1194.6,608.1c-0.1-2.8-2.8-4-0.8-8.4c-2.7,3-2.3,4.4-2.1,6.8c-0.4-1.1-0.9-1.9-2-2.8        c0.5,1.4,0.3,2.8-0.4,4.4H1194.6L1194.6,608.1z"
                />
                <path
                  class="st30"
                  d="M1193.1,608.4c0-2-2.2-2.8-0.6-6c-2.1,2.1-1.7,3.1-1.6,4.9c-0.3-0.8-0.7-1.3-1.6-2c0.4,1,0.2,2-0.3,3.1        H1193.1L1193.1,608.4z"
                />
              </g>
              <g>
                <path
                  class="st29"
                  d="M1167.6,617c-0.1-2.8-2.8-4-0.8-8.4c-2.7,3-2.3,4.4-2.1,6.8c-0.4-1.1-0.9-1.9-2-2.8        c0.5,1.4,0.3,2.8-0.4,4.4H1167.6L1167.6,617z"
                />
                <path
                  class="st30"
                  d="M1166,617.3c0-2-2.2-2.8-0.6-6c-2.1,2.1-1.7,3.1-1.6,4.9c-0.3-0.8-0.7-1.3-1.6-2c0.4,1,0.2,2-0.3,3.1        H1166L1166,617.3z"
                />
              </g>
              <g>
                <path
                  class="st29"
                  d="M1153.5,632.2c-0.1-2.8-2.8-4-0.8-8.4c-2.7,3-2.3,4.4-2.1,6.8c-0.4-1.1-0.9-1.9-2-2.8        c0.5,1.4,0.3,2.8-0.4,4.4H1153.5L1153.5,632.2z"
                />
                <path
                  class="st30"
                  d="M1152,632.5c0-2-2.2-2.8-0.6-6c-2.1,2.1-1.7,3.1-1.6,4.9c-0.3-0.8-0.7-1.3-1.6-2c0.4,1,0.2,2-0.3,3.1        H1152L1152,632.5z"
                />
              </g>
              <g>
                <path
                  class="st29"
                  d="M1148,627.8c-0.1-2.8-2.8-4-0.8-8.4c-2.7,3-2.3,4.4-2.1,6.8c-0.4-1.1-0.9-1.9-2-2.8        c0.5,1.4,0.3,2.8-0.4,4.4H1148L1148,627.8z"
                />
                <path
                  class="st30"
                  d="M1146.4,628.1c0-2-2.2-2.8-0.6-6c-2.1,2.1-1.7,3.1-1.6,4.9c-0.3-0.8-0.7-1.3-1.6-2c0.4,1,0.2,2-0.3,3.1        H1146.4L1146.4,628.1z"
                />
              </g>
              <g>
                <path
                  class="st29"
                  d="M1111.1,647.8c-0.1-2.8-2.8-4-0.8-8.4c-2.7,3-2.3,4.4-2.1,6.8c-0.4-1.1-0.9-1.9-2-2.8        c0.5,1.4,0.3,2.8-0.4,4.4H1111.1L1111.1,647.8z"
                />
                <path
                  class="st30"
                  d="M1109.6,648.2c0-2-2.2-2.8-0.6-6c-2.1,2.1-1.7,3.1-1.6,4.9c-0.3-0.8-0.7-1.3-1.6-2c0.4,1,0.2,2-0.3,3.1        H1109.6L1109.6,648.2z"
                />
              </g>
            </g>
          </g>
        </g>
      </g>
      <g>
        <g>
          <g>
            <defs>
              <path
                id="SVGID_00000008129815099050992490000000633648844790589848_"
                d="M1275.6,570.5l-39.3-20.9c0,0,11.4-53.2,11.6-55.4       c0.2-2.2,23.8-56.1,23.8-56.1l45.9-5.6l22.3,27.3l17.1,41.7l10.4,16.3L1275.6,570.5z"
              />
            </defs>
            <clipPath
              id="SVGID_00000044892291684148123180000014928221599553655468_"
            >
              <use
                xmlns:xlink="http://www.w3.org/1999/xlink"
                xlink:href="#SVGID_00000008129815099050992490000000633648844790589848_"
                style="overflow: visible"
              />
            </clipPath>
            <g
              style="
                clip-path: url(#SVGID_00000044892291684148123180000014928221599553655468_);
              "
            >
              <g class="st9">
                <path
                  class="st10"
                  d="M1266.4,526.6c-4.8,2.8-6.5,5.6-6.4,9.5c0.1,3.2-1.8,6-2.6,9.2c-0.8,3.2-1.9,5.8,0.4,8.9        c1.9,2.5,5.2,5.1,9.2,6.6c2.8,1.1,5.7,0.7,8.6,1.4c0.8,0.3,1.5,0.6,2.2,0.8c1.3,0.7,2.3,1.6,3.7,2.5c3.4,2,6.9,3.6,11.9,3.3        c5.9-0.4,11.1-1.5,16.6-2.6c4.7-1,9.4-0.7,13.9-2.3c1-0.4,1.9-0.8,2.6-1.2c3.4-2,4.8-4.7,7.5-7.1c2.7-2.4,7.5-4.9,7.7-8        c0.1-1.6-1.6-3.3-2-4.9c-0.4-1.4-0.8-2.8-1.2-4.2c-0.3-1.2-0.4-2.5-0.6-3.8c0.2-0.5,0.3-0.9,0.3-1.4c-0.1-3.3-4.6-5.8-9.4-7.1        c-2.4-0.6-5.1-0.9-7.7-1.2c-3.1-1.3-6.5-2.3-10-3c-4.1-1.9-8-3.5-14-3.5c-6.5,0-11.1,3.2-16.8,4.6        C1275.4,524.3,1270.3,524.4,1266.4,526.6z"
                />
              </g>
              <g>
                <g>
                  <path
                    class="st11"
                    d="M1298.4,528c0.5-2.4,0.2-8,0.3-11.8c0-1.9,0.2-3.9,0.4-5.8c1.1,0.2,2.1,0.3,3.2,0.4         c-0.4,0.6-0.6,1.3-0.8,2c-0.9,3.6-1.1,7.6-0.8,11.3c0.2,2.9,0.6,5.7,0.6,8.6c0,1.8,0,3.5,0.7,5.3c0.8,1.9,2.1,3.5,3.6,4.8         c0,0.1,0.1,0.1,0.1,0.2c0.1,0.1,0.1,0.2,0.2,0.4c-0.7-0.3-1.5-0.5-2.2-0.4c-0.6,0.2-0.7,0.7-1.1,1c-1.4,0.8-1.9,0.3-3.3,1.3         c-0.5,0.4-1.1,0.7-1.6,0.8c0-0.5,0-1-0.1-1.3c-0.2-1.8-0.1-4.5-0.3-7.5C1297.2,534.2,1298,530.4,1298.4,528z"
                  />
                  <path
                    class="st12"
                    d="M1290.7,544.3c0.2-0.2,0.4-0.3,0.5-0.5c0.1-0.1,0.1-0.2,0.2-0.2c0.3-0.7,0.9-1.5,1.2-2.2         c0.6-0.8,1.1-1.7,1.5-2.6c1-2.2,1.4-4.6,1.7-7c0-0.2,0.1-0.4,0.1-0.7c0.1-0.3,0.1-0.6,0.2-0.9c1.8-5.4,0.8-11.3,1.4-16.9         c0.2-1,0.5-2.1,0.7-3.1c0.3,0.1,0.6,0.1,0.9,0.2c-0.2,1.9-0.3,3.8-0.4,5.8c-0.1,3.9,0.2,9.4-0.3,11.8c-0.5,2.4-1.3,6.3-1,9.3         c0.2,3,0.2,5.7,0.3,7.5c0,0.3,0,0.8,0.1,1.3c-1,0.3-1.9,0.1-2.4-1.4c-0.1-0.3-0.3-0.5-0.6-0.6c-1.4-0.1-2.5,1.1-3.7,1.1         c0.1,0-0.4-0.2-0.8-0.3C1290.5,544.7,1290.6,544.5,1290.7,544.3z"
                  />
                </g>
                <g>
                  <path
                    class="st13"
                    d="M1308,530.4c-1.5-0.2-3.8,0.3-5.3-0.2c-0.9-0.3-1.7-1.6-2.6-1.8c-1.4-0.4-3.4,0.7-5.1,0.9         c-1.8,0.2-3.8-0.1-5.6-0.4c-2.1-0.4-3.2-0.8-4.1-2.3c-1.8,0-3.2,0.2-4.8-1.1c-2.1-1.6-4.4-5.1-6.1-7.2         c-1.4-1.9-1.4-2.5-2.3-4.6c-1.2-2.9-2.4-4-4.6-6.1c-2.1-2-3.7-4-4.4-7c-0.6-2.4-0.1-4.3-1.2-6.6c-1.2-2.5-2.5-3.6-2.3-6.6         c0.1-1.3,0.6-2.5,1.2-3.6c0.1,1.1,0.1,2.1,0.4,3.3c0.7,2.9,2.3,5,4.4,7c2.2,2.1,3.4,3.2,4.6,6.1c0.9,2.2,0.9,2.7,2.3,4.6         c1.6,2.2,4,5.6,6.1,7.2c1.6,1.3,3,1.1,4.8,1.1c0.9,1.5,2,1.9,4.1,2.3c1.8,0.4,3.7,0.6,5.6,0.4c1.7-0.2,3.7-1.3,5.1-0.9         c0.9,0.2,1.7,1.5,2.6,1.8c1.5,0.5,3.8,0,5.3,0.2c2.9,0.3,4.2,0.6,6.8-1.4c1.6-1.3,2.5-3.3,3.9-4.6c1.6-1.3,3.2-1,4.7-1.8         c1.9-1,3.6-2.4,5.2-3.8c1.8-1.7,3.2-3.8,5-5.5c1.1-1.1,2.7-1.9,3.5-3.3c1.2-2.1,1.1-4.5,1.7-6.7c0.7-2.3,1-4.6,1.1-6.9         c1.9,6.7,2.8,13.6,0.8,20.3c-0.6,2.2-0.6,4.6-1.7,6.7c-0.8,1.4-2.4,2.2-3.5,3.3c-1.8,1.7-3.2,3.8-5,5.5         c-1.6,1.4-3.3,2.9-5.2,3.8c-1.5,0.8-3.1,0.5-4.7,1.8c-1.5,1.2-2.4,3.3-3.9,4.6C1312.2,531,1310.8,530.7,1308,530.4z"
                  />
                  <g>
                    <path
                      class="st14"
                      d="M1316.4,457.1c-4.2-1.6-7.2-4.1-11.7-4.5c-3.3-0.3-7.3,0-10.5,1c-3,1-6,3.1-8.4,5          c-3.1,2.5-6.6,5-9.3,8c-2.3,2.5-4.9,3.6-7.3,5.8c-2.3,2-4.1,5.6-5.8,8.3c-0.7,0.5-1.4,0.9-1.8,1.5s-0.6,1.2-0.9,1.9          c0-0.1,0-0.2,0-0.4c0.5-1,1.2-2,1.7-3.1c0.6-1.1,0.8-2.7,1.5-3.8c0.4-0.6,1-1,1.6-1.5c1.6-2.7,3.3-6.3,5.4-8.3          c2.2-2.1,4.6-3.3,6.8-5.8c2.5-3,5.7-5.5,8.6-8c2.2-1.9,5-4,7.8-5c2.9-1,6.7-1.3,9.7-1c4.2,0.4,7,2.9,10.9,4.5          c5.6,2.4,10.4,6.4,14.7,10.8c2.4,2.4,3.8,5,4.9,7.8c-0.7-0.8-1.4-1.7-2.2-2.5C1327.6,463.6,1322.5,459.5,1316.4,457.1z"
                    />
                    <path
                      class="st15"
                      d="M1338,483c-0.1,2.3-0.4,4.6-1.1,6.9c-0.6,2.2-0.6,4.6-1.7,6.7c-0.8,1.4-2.4,2.2-3.5,3.3          c-1.8,1.7-3.2,3.8-5,5.5c-1.6,1.4-3.3,2.9-5.2,3.8c-1.5,0.8-3.1,0.5-4.7,1.8c-1.5,1.2-2.4,3.3-3.9,4.6          c-2.5,2-3.9,1.7-6.8,1.4c-1.5-0.2-3.8,0.3-5.3-0.2c-0.9-0.3-1.7-1.6-2.6-1.8c-1.4-0.4-3.4,0.7-5.1,0.9          c-1.8,0.2-3.8-0.1-5.6-0.4c-2.1-0.4-3.2-0.8-4.1-2.3c-1.8,0-3.2,0.2-4.8-1.1c-2.1-1.6-4.4-5.1-6.1-7.2          c-1.4-1.9-1.4-2.5-2.3-4.6c-1.2-2.9-2.4-4-4.6-6.1c-2.1-2-3.7-4-4.4-7c-0.3-1.1-0.3-2-0.4-3c0.2-0.7,0.5-1.4,0.9-1.9          c0.4-0.6,1.1-1,1.8-1.5c1.7-2.7,3.5-6.3,5.8-8.3c2.4-2.1,5-3.3,7.3-5.8c2.7-3,6.1-5.5,9.3-8c2.3-1.9,5.4-4,8.4-5          c3.2-1,7.2-1.3,10.5-1c4.6,0.4,7.5,2.9,11.7,4.5c6.1,2.4,11.2,6.4,15.8,10.8c0.9,0.8,1.6,1.6,2.2,2.5          c1.4,3.7,2.1,7.6,3.2,11.6C1337.8,482.3,1337.9,482.6,1338,483z"
                    />
                  </g>
                </g>
              </g>
            </g>
          </g>
        </g>
      </g>
      <g>
        <g>
          <g>
            <defs>
              <polygon
                id="SVGID_00000139271396552304316160000000559401776904349599_"
                points="762.5,617 876.1,682.1 922.7,644.8        914,567.4 888.9,485.9 821.9,470.4 759.2,507.3 736.7,556.6      "
              />
            </defs>
            <clipPath
              id="SVGID_00000059276465433321473540000017781661771971148218_"
            >
              <use
                xmlns:xlink="http://www.w3.org/1999/xlink"
                xlink:href="#SVGID_00000139271396552304316160000000559401776904349599_"
                style="overflow: visible"
              />
            </clipPath>
            <g
              style="
                clip-path: url(#SVGID_00000059276465433321473540000017781661771971148218_);
              "
            >
              <g class="st9">
                <path
                  class="st10"
                  d="M781.4,614.1c-7.6,4.4-10.2,8.9-10,15c0.1,5.1-2.9,9.5-4.1,14.5c-1.2,5.1-3,9.1,0.6,14        c2.9,4,8.2,8,14.6,10.4c4.5,1.7,9,1.2,13.6,2.2c1.3,0.5,2.4,0.9,3.5,1.3c2.1,1.1,3.6,2.6,5.8,3.9c5.3,3.2,10.9,5.6,18.8,5.2        c9.3-0.6,17.5-2.3,26.2-4.1c7.3-1.5,14.9-1.2,21.9-3.7c1.6-0.6,3-1.2,4.2-1.9c5.4-3.1,7.6-7.4,11.8-11.3        c4.2-3.8,11.9-7.8,12.1-12.6c0.1-2.6-2.5-5.2-3.2-7.7c-0.6-2.2-1.3-4.4-1.9-6.6c-0.5-1.8-0.6-3.9-1-6c0.3-0.7,0.5-1.5,0.4-2.3        c-0.2-5.2-7.2-9.1-14.9-11.1c-3.8-1-8-1.4-12.1-1.9c-4.9-2-10.2-3.6-15.8-4.7c-6.5-3-12.7-5.5-22.1-5.5        c-10.3,0-17.5,5-26.5,7.2C795.7,610.5,787.7,610.5,781.4,614.1z"
                />
              </g>
              <g>
                <g>
                  <path
                    class="st11"
                    d="M832,616.2c0.7-3.8,0.3-12.6,0.4-18.6c0.1-3.1,0.3-6.2,0.6-9.1c1.7,0.3,3.4,0.5,5.1,0.7         c-0.6,0.9-1,2-1.3,3.2c-1.4,5.7-1.7,12-1.3,17.8c0.3,4.5,0.9,9.1,1,13.6c0.1,2.9,0.1,5.6,1.2,8.3c1.2,3,3.3,5.5,5.8,7.6         c0.1,0.1,0.1,0.2,0.2,0.3c0.1,0.2,0.2,0.4,0.3,0.6c-1.2-0.5-2.3-0.8-3.4-0.6c-1,0.2-1.1,1.2-1.8,1.5c-2.2,1.2-3,0.4-5.2,2         c-0.8,0.6-1.7,1.1-2.5,1.3c0-0.8,0-1.6-0.1-2.1c-0.3-2.8-0.1-7.1-0.5-11.8C830.1,626.1,831.3,620.1,832,616.2z"
                  />
                  <path
                    class="st12"
                    d="M819.8,642c0.3-0.3,0.6-0.5,0.8-0.8c0.1-0.1,0.2-0.3,0.3-0.4c0.5-1.2,1.4-2.3,2-3.4         c0.9-1.3,1.7-2.7,2.4-4.1c1.5-3.4,2.1-7.3,2.6-11c0.1-0.4,0.1-0.7,0.2-1.1c0.1-0.4,0.2-0.9,0.3-1.4         c2.8-8.4,1.3-17.8,2.2-26.7c0.3-1.6,0.7-3.3,1.1-4.9c0.5,0.1,0.9,0.2,1.4,0.2c-0.3,3-0.5,6.1-0.6,9.1         c-0.1,6.1,0.3,14.8-0.4,18.6s-2,9.9-1.6,14.6c0.4,4.7,0.3,9,0.5,11.8c0,0.5,0.1,1.3,0.1,2.1c-1.6,0.5-3.1,0.1-3.7-2.2         c-0.1-0.5-0.4-0.9-1-0.9c-2.2-0.2-4,1.7-5.8,1.7c0.1,0-0.6-0.2-1.3-0.5C819.4,642.6,819.6,642.3,819.8,642z"
                  />
                </g>
                <g>
                  <path
                    class="st13"
                    d="M847.1,620c-2.4-0.3-6.1,0.5-8.4-0.3c-1.5-0.5-2.8-2.5-4.1-2.8c-2.2-0.6-5.3,1.2-8.1,1.5         c-2.9,0.3-6-0.1-8.8-0.7c-3.3-0.7-5-1.3-6.4-3.7c-2.8,0-5,0.2-7.5-1.8c-3.3-2.6-7-8-9.6-11.4c-2.2-3-2.3-3.9-3.6-7.3         c-1.8-4.6-3.7-6.4-7.2-9.6c-3.4-3.2-5.9-6.4-7-11c-0.9-3.8-0.1-6.8-1.9-10.5c-1.9-3.9-4-5.8-3.6-10.4         c0.2-2.1,0.9-3.9,1.9-5.7c0.1,1.7,0.2,3.4,0.6,5.3c1.1,4.6,3.6,7.8,7,11c3.5,3.3,5.4,5.1,7.2,9.6c1.4,3.4,1.4,4.3,3.6,7.3         c2.6,3.4,6.3,8.8,9.6,11.4c2.5,2,4.7,1.7,7.5,1.8c1.4,2.4,3.1,3,6.4,3.7c2.8,0.6,5.9,1,8.8,0.7c2.7-0.3,5.8-2.1,8.1-1.5         c1.4,0.4,2.6,2.3,4.1,2.8c2.3,0.8,6,0,8.4,0.3c4.5,0.5,6.6,1,10.7-2.2c2.5-2,3.9-5.3,6.2-7.2c2.5-2.1,5-1.7,7.4-2.9         c3-1.5,5.7-3.8,8.2-6.1c2.9-2.6,5-5.9,7.9-8.7c1.7-1.7,4.3-3,5.6-5.2c1.8-3.2,1.7-7.1,2.7-10.5c1.1-3.6,1.5-7.2,1.7-10.9         c3,10.6,4.4,21.5,1.3,32.1c-1,3.4-0.9,7.3-2.7,10.5c-1.2,2.2-3.8,3.5-5.6,5.2c-2.8,2.7-5,6-7.9,8.7c-2.5,2.3-5.2,4.5-8.2,6.1         c-2.4,1.2-4.9,0.8-7.4,2.9c-2.3,1.9-3.7,5.2-6.2,7.2C853.7,621,851.6,620.5,847.1,620z"
                  />
                  <g>
                    <path
                      class="st14"
                      d="M860.5,504.4c-6.6-2.6-11.3-6.5-18.5-7.1c-5.2-0.4-11.5,0-16.5,1.6c-4.7,1.5-9.5,4.9-13.2,7.8          c-5,3.9-10.4,8-14.7,12.6c-3.6,4-7.7,5.8-11.5,9.2c-3.6,3.2-6.5,8.8-9.1,13.1c-1.1,0.8-2.2,1.5-2.8,2.4s-1,2-1.4,3          c0-0.2,0-0.4,0-0.6c0.9-1.6,1.9-3.2,2.7-4.9c0.9-1.8,1.2-4.2,2.3-6c0.6-0.9,1.6-1.6,2.6-2.4c2.5-4.3,5.2-10,8.5-13.1          c3.5-3.4,7.3-5.2,10.7-9.2c4-4.7,9-8.7,13.6-12.6c3.4-2.9,7.9-6.3,12.3-7.8c4.6-1.6,10.5-2.1,15.4-1.6          c6.7,0.6,11,4.6,17.2,7.1c8.9,3.7,16.4,10.2,23.1,17c3.8,3.8,6,7.9,7.7,12.3c-1-1.3-2.2-2.6-3.5-3.9          C878.1,514.6,870,508.2,860.5,504.4z"
                    />
                    <path
                      class="st15"
                      d="M894.4,545.2c-0.1,3.6-0.6,7.3-1.7,10.9c-1,3.4-0.9,7.3-2.7,10.5c-1.2,2.2-3.8,3.5-5.6,5.2          c-2.8,2.7-5,6-7.9,8.7c-2.5,2.3-5.2,4.5-8.2,6.1c-2.4,1.2-4.9,0.8-7.4,2.9c-2.3,1.9-3.7,5.2-6.2,7.2          c-4,3.2-6.1,2.7-10.7,2.2c-2.4-0.3-6.1,0.5-8.4-0.3c-1.5-0.5-2.8-2.5-4.1-2.8c-2.2-0.6-5.3,1.2-8.1,1.5          c-2.9,0.3-6-0.1-8.8-0.7c-3.3-0.7-5-1.3-6.4-3.7c-2.8,0-5,0.2-7.5-1.8c-3.3-2.6-7-8-9.6-11.4c-2.2-3-2.3-3.9-3.6-7.3          c-1.8-4.6-3.7-6.4-7.2-9.6c-3.4-3.2-5.9-6.4-7-11c-0.4-1.7-0.5-3.2-0.6-4.7c0.4-1.1,0.8-2.1,1.4-3c0.6-0.9,1.7-1.6,2.8-2.4          c2.7-4.3,5.6-10,9.1-13.1c3.8-3.4,7.9-5.2,11.5-9.2c4.3-4.7,9.7-8.7,14.7-12.6c3.7-2.9,8.5-6.3,13.2-7.8          c5-1.6,11.3-2.1,16.5-1.6c7.2,0.6,11.9,4.6,18.5,7.1c9.6,3.7,17.6,10.2,24.9,17c1.3,1.3,2.5,2.6,3.5,3.9          c2.2,5.8,3.3,11.9,5.1,18.3C894.1,544.1,894.3,544.7,894.4,545.2z"
                    />
                  </g>
                </g>
              </g>
            </g>
          </g>
        </g>
      </g>
      <g>
        <g>
          <g>
            <defs>
              <polygon
                id="SVGID_00000083075767532824168060000002069128892976368266_"
                points="577.3,507.5 643.6,545.6 677.9,520        682.1,459 658.9,363 601.3,361.9 548.6,403.6 540.2,457.2 596.1,496.7      "
              />
            </defs>
            <clipPath
              id="SVGID_00000147214658906738803550000003507405850923614391_"
            >
              <use
                xmlns:xlink="http://www.w3.org/1999/xlink"
                xlink:href="#SVGID_00000083075767532824168060000002069128892976368266_"
                style="overflow: visible"
              />
            </clipPath>
            <g
              style="
                clip-path: url(#SVGID_00000147214658906738803550000003507405850923614391_);
              "
            >
              <g class="st9">
                <path
                  class="st10"
                  d="M569.7,491.7c-6.2,3.6-8.3,7.2-8.1,12.1c0.1,4.1-2.3,7.7-3.3,11.7c-1,4.1-2.4,7.4,0.5,11.3        c2.4,3.3,6.6,6.5,11.8,8.4c3.6,1.3,7.3,0.9,11,1.8c1,0.4,1.9,0.7,2.8,1.1c1.7,0.9,2.9,2.1,4.7,3.2c4.3,2.6,8.8,4.6,15.2,4.2        c7.5-0.5,14.1-1.9,21.2-3.3c5.9-1.2,12.1-1,17.7-3c1.3-0.5,2.4-1,3.4-1.5c4.4-2.5,6.1-6,9.6-9.1c3.4-3.1,9.6-6.3,9.8-10.2        c0.1-2.1-2-4.2-2.6-6.2c-0.5-1.8-1-3.6-1.5-5.4c-0.4-1.5-0.5-3.2-0.8-4.9c0.2-0.6,0.4-1.2,0.3-1.8c-0.2-4.2-5.8-7.3-12-9        c-3-0.8-6.5-1.1-9.8-1.5c-4-1.6-8.3-2.9-12.7-3.8c-5.3-2.5-10.2-4.4-17.9-4.4c-8.3,0-14.1,4-21.4,5.8        C581.2,488.8,574.7,488.8,569.7,491.7z"
                />
              </g>
              <g>
                <g>
                  <path
                    class="st11"
                    d="M610.6,493.4c0.6-3.1,0.2-10.1,0.3-15.1c0.1-2.5,0.3-5,0.5-7.4c1.4,0.2,2.7,0.4,4.1,0.5         c-0.4,0.8-0.8,1.6-1,2.6c-1.2,4.6-1.4,9.7-1.1,14.4c0.2,3.7,0.7,7.3,0.8,11c0.1,2.3,0,4.5,0.9,6.7c1,2.4,2.6,4.4,4.6,6.2         c0,0.1,0.1,0.1,0.1,0.2c0.1,0.2,0.2,0.3,0.2,0.5c-0.9-0.4-1.9-0.7-2.8-0.5c-0.8,0.2-0.9,0.9-1.4,1.2c-1.8,1-2.4,0.4-4.2,1.6         c-0.6,0.5-1.4,0.9-2,1.1c0-0.7,0-1.3-0.1-1.7c-0.2-2.3-0.1-5.7-0.4-9.6C609,501.4,610,496.5,610.6,493.4z"
                  />
                  <path
                    class="st12"
                    d="M600.7,514.2c0.2-0.2,0.5-0.4,0.7-0.7c0.1-0.1,0.2-0.2,0.2-0.3c0.4-1,1.1-1.9,1.6-2.8         c0.7-1.1,1.4-2.2,1.9-3.3c1.2-2.7,1.7-5.9,2.1-8.9c0-0.3,0.1-0.6,0.1-0.9c0.1-0.4,0.2-0.7,0.3-1.1c2.3-6.8,1-14.4,1.8-21.6         c0.3-1.3,0.6-2.7,0.9-4c0.4,0.1,0.8,0.1,1.1,0.2c-0.2,2.4-0.4,4.9-0.5,7.4c-0.1,4.9,0.2,12-0.3,15.1c-0.6,3.1-1.6,8-1.3,11.8         s0.2,7.3,0.4,9.6c0,0.4,0.1,1,0.1,1.7c-1.3,0.4-2.5,0.1-3-1.8c-0.1-0.4-0.3-0.7-0.8-0.7c-1.8-0.2-3.2,1.4-4.7,1.4         c0.1,0-0.5-0.2-1-0.4C600.4,514.7,600.5,514.5,600.7,514.2z"
                  />
                </g>
                <g>
                  <path
                    class="st13"
                    d="M622.7,496.5c-1.9-0.2-4.9,0.4-6.8-0.2c-1.2-0.4-2.2-2-3.3-2.3c-1.8-0.5-4.3,0.9-6.5,1.2         c-2.3,0.3-4.8-0.1-7.1-0.5c-2.7-0.6-4-1-5.2-3c-2.3,0-4,0.2-6.1-1.4c-2.6-2.1-5.7-6.5-7.7-9.2c-1.8-2.4-1.8-3.1-2.9-5.9         c-1.5-3.7-3-5.1-5.8-7.8c-2.7-2.5-4.8-5.1-5.6-8.9c-0.7-3.1-0.1-5.5-1.5-8.4c-1.5-3.2-3.2-4.7-2.9-8.4         c0.1-1.7,0.8-3.2,1.5-4.6c0.1,1.3,0.1,2.7,0.5,4.2c0.9,3.7,2.9,6.3,5.6,8.9c2.8,2.7,4.4,4.1,5.8,7.8c1.1,2.7,1.1,3.5,2.9,5.9         c2.1,2.7,5.1,7.1,7.7,9.2c2.1,1.6,3.8,1.4,6.1,1.4c1.1,1.9,2.5,2.4,5.2,3c2.3,0.5,4.8,0.8,7.1,0.5c2.2-0.3,4.7-1.7,6.5-1.2         c1.1,0.3,2.1,1.9,3.3,2.3c1.9,0.7,4.8,0,6.8,0.2c3.7,0.4,5.4,0.8,8.6-1.8c2-1.6,3.2-4.3,5-5.8c2-1.7,4-1.3,6-2.3         c2.4-1.2,4.6-3.1,6.6-4.9c2.3-2.1,4.1-4.8,6.4-7c1.4-1.4,3.5-2.4,4.5-4.2c1.5-2.6,1.4-5.7,2.2-8.5c0.9-2.9,1.2-5.8,1.4-8.8         c2.4,8.6,3.6,17.4,1.1,25.9c-0.8,2.8-0.7,5.9-2.2,8.5c-1,1.8-3.1,2.8-4.5,4.2c-2.3,2.2-4,4.9-6.4,7c-2,1.8-4.2,3.7-6.6,4.9         c-2,1-4,0.7-6,2.3c-1.9,1.6-3,4.2-5,5.8C628.1,497.3,626.4,496.9,622.7,496.5z"
                  />
                  <g>
                    <path
                      class="st14"
                      d="M633.5,403.1c-5.4-2.1-9.1-5.3-14.9-5.8c-4.2-0.4-9.3,0-13.4,1.3c-3.8,1.2-7.7,4-10.7,6.3          c-4,3.2-8.4,6.4-11.8,10.2c-2.9,3.2-6.2,4.7-9.3,7.4c-2.9,2.6-5.2,7.1-7.4,10.6c-0.9,0.6-1.8,1.2-2.2,1.9          c-0.5,0.7-0.8,1.6-1.1,2.4c0-0.2,0-0.3,0-0.5c0.7-1.3,1.5-2.6,2.2-4c0.7-1.5,1-3.4,1.9-4.8c0.5-0.7,1.3-1.3,2.1-1.9          c2-3.5,4.2-8,6.8-10.6c2.8-2.7,5.9-4.2,8.6-7.4c3.2-3.8,7.3-7,11-10.2c2.8-2.4,6.4-5.1,9.9-6.3c3.7-1.3,8.5-1.7,12.4-1.3          c5.4,0.5,8.9,3.7,13.9,5.8c7.2,3,13.2,8.2,18.7,13.7c3,3.1,4.9,6.4,6.2,9.9c-0.8-1.1-1.8-2.1-2.8-3.2          C647.8,411.3,641.3,406.1,633.5,403.1z"
                    />
                    <path
                      class="st15"
                      d="M661,436c-0.1,2.9-0.5,5.9-1.4,8.8c-0.8,2.8-0.7,5.9-2.2,8.5c-1,1.8-3.1,2.8-4.5,4.2          c-2.3,2.2-4,4.9-6.4,7c-2,1.8-4.2,3.7-6.6,4.9c-2,1-4,0.7-6,2.3c-1.9,1.6-3,4.2-5,5.8c-3.2,2.6-5,2.2-8.6,1.8          c-1.9-0.2-4.9,0.4-6.8-0.2c-1.2-0.4-2.2-2-3.3-2.3c-1.8-0.5-4.3,0.9-6.5,1.2c-2.3,0.3-4.8-0.1-7.1-0.5c-2.7-0.6-4-1-5.2-3          c-2.3,0-4,0.2-6.1-1.4c-2.6-2.1-5.7-6.5-7.7-9.2c-1.8-2.4-1.8-3.1-2.9-5.9c-1.5-3.7-3-5.1-5.8-7.8c-2.7-2.5-4.8-5.1-5.6-8.9          c-0.3-1.4-0.4-2.6-0.5-3.8c0.3-0.9,0.6-1.7,1.1-2.4c0.5-0.7,1.4-1.3,2.2-1.9c2.1-3.5,4.5-8,7.4-10.6          c3.1-2.7,6.4-4.2,9.3-7.4c3.4-3.8,7.8-7,11.8-10.2c3-2.4,6.9-5.1,10.7-6.3c4-1.3,9.2-1.7,13.4-1.3c5.8,0.5,9.6,3.7,14.9,5.8          c7.7,3,14.3,8.2,20.1,13.7c1.1,1,2,2.1,2.8,3.2c1.8,4.7,2.6,9.6,4.1,14.8C660.7,435.2,660.8,435.6,661,436z"
                    />
                  </g>
                </g>
              </g>
            </g>
          </g>
        </g>
      </g>
      <g>
        <line class="st34" x1="1087.5" y1="733" x2="1362.6" y2="573.3" />
        <circle class="st1" cx="1092" cy="730.5" r="9.7" />
        <circle class="st35" cx="1359.3" cy="575.2" r="9.1" />
        <circle class="st3" cx="1092" cy="730.5" r="7.3" />
      </g>
    </g>
    <g id="Layer_1">
      <g>
        <a
          :href="links.frontage"
          @focus="handleFocus('frontage')"
          @mouseenter="handleFocus('frontage')"
          @mouseleave="focus['frontage'] = false"
          :style="focus['frontage'] ? focusStyles : undefined"
        >
          <g>
            <rect x="1100.7" y="106.2" class="st66" width="261" height="80.1" />
            <path
              class="st67"
              d="M1120.5,171.4c-0.4,0-0.6,0.4-0.6,0.7c0,0.4,0.4,0.6,0.7,0.6h45.7c0.4,0,0.7-0.3,0.7-0.7     c0-0.4-0.3-0.7-0.7-0.7c0,0,0,0,0,0h-45.7C1120.6,171.4,1120.5,171.4,1120.5,171.4 M1130.5,158.2c-0.4,0-0.6,0.3-0.6,0.7     c0,0.4,0.3,0.6,0.7,0.6h4.7c0.4,0,0.7-0.3,0.7-0.7c0-0.4-0.3-0.7-0.7-0.7h0H1130.5C1130.5,158.2,1130.5,158.2,1130.5,158.2      M1151.3,131.6c-0.4,0.1-0.6,0.4-0.6,0.8c0,0.1,0,0.1,0.1,0.2c2.4,5,5.2,8,9.6,12.2c-0.1,0.7-0.3,1.1-0.6,1.3     c-0.4,0.2-1,0.3-1.8,0.1c-1.5-0.3-3.2-1.6-3.7-2.5c-0.2-0.3-0.5-0.5-0.9-0.3c-0.2,0.1-0.4,0.3-0.4,0.6v2.4c0,0.1,0,0.2,0.1,0.3     l1.8,4.1l1.4,14.2c-0.6,0.2-1.2,0.3-1.8,0.2c-0.6-0.2-1.2-0.6-1.5-1.4c0,0,0,0,0,0l-0.7-7.1c-0.1-1.1-0.5-2.8-1-3.8c0,0,0,0,0,0     l-1.6-3.9c-0.1-0.3-0.5-0.5-0.9-0.4c-0.2,0.1-0.3,0.2-0.4,0.4l-2.1,6.6l-5.2,9.7c-0.8-0.2-1.4-0.7-1.7-1.3     c-0.4-0.7-0.4-1.6-0.1-2.3l5.4-14.2c0-0.1,0-0.2,0-0.2l0.2-6.3c0-0.4-0.3-0.7-0.7-0.7c-0.2,0-0.5,0.1-0.6,0.3l-2.9,5.7     c-0.2,0.3-0.5,0.5-0.7,0.5c-0.3,0-0.5-0.1-0.6-0.3c-0.2-0.2-0.3-0.5-0.4-0.8c-0.1-0.7,0-1.7,0.3-2.4l4.8-10.5     c0.2-0.3,0-0.7-0.3-0.9c-0.3-0.2-0.7,0-0.9,0.3c0,0,0,0,0,0l-4.8,10.5c-0.5,1-0.6,2.2-0.4,3.2c0.1,0.5,0.3,1,0.7,1.4     c0.4,0.4,0.9,0.7,1.5,0.8c0.9,0.1,1.7-0.5,2-1.2l1.5-3l-0.1,3.2l-5.4,14.1c-0.4,1-0.3,2.3,0.2,3.4c0.5,1.1,1.6,2,3.1,2.2     c0.3,0,0.5-0.1,0.7-0.3l5.5-10.3c0,0,0-0.1,0-0.1l1.6-4.9l0.9,2.3c0.4,0.9,0.8,2.6,0.9,3.5l0.7,7.3c0,0,0,0.1,0,0.1     c0.4,1.2,1.4,2,2.4,2.3c1,0.3,2.1,0.1,3.1-0.4c0.2-0.1,0.4-0.4,0.4-0.7l-0.6-5.8h19.2c0.4,0,0.7-0.3,0.7-0.7     c0-0.4-0.3-0.7-0.7-0.7h0h-19.4l-0.8-7.8c0-0.1,0-0.1,0-0.2l-1.7-4v-0.4c0.9,0.8,2.1,1.4,3.3,1.7c0.9,0.2,1.9,0.2,2.7-0.2     c0.8-0.5,1.3-1.4,1.3-2.7c0-0.2-0.1-0.4-0.2-0.5c-4.5-4.4-7.1-7.2-9.5-12.1C1151.8,131.7,1151.5,131.6,1151.3,131.6      M1148.6,121.1c2.4,0,4.3,1.9,4.3,4.3c0,2.4-1.9,4.3-4.3,4.3c-2.4,0-4.3-1.9-4.3-4.3C1144.3,123,1146.2,121.1,1148.6,121.1      M1148.6,119.7c-3.1,0-5.6,2.5-5.6,5.6c0,3.1,2.5,5.6,5.6,5.6c3.1,0,5.6-2.5,5.6-5.6C1154.2,122.3,1151.7,119.7,1148.6,119.7"
            />
            <g>
              <path
                class="st67"
                d="M1195.4,142.1h-2.9v-13.7h7.9v2.4h-5v3.5h4.7v2.4h-4.7V142.1z"
              />
              <path
                class="st67"
                d="M1206,136.8v5.3h-2.9v-13.7h4c1.9,0,3.2,0.3,4.1,1c0.9,0.7,1.3,1.7,1.3,3.1c0,0.8-0.2,1.5-0.7,2.2     c-0.4,0.6-1.1,1.1-1.9,1.5c2.1,3.1,3.4,5.1,4,6h-3.2l-3.3-5.3H1206z M1206,134.4h0.9c0.9,0,1.6-0.2,2-0.5     c0.4-0.3,0.7-0.8,0.7-1.4c0-0.7-0.2-1.1-0.7-1.4c-0.4-0.3-1.1-0.4-2.1-0.4h-0.9V134.4z"
              />
              <path
                class="st67"
                d="M1228.3,135.2c0,2.3-0.6,4-1.7,5.2c-1.1,1.2-2.7,1.8-4.8,1.8s-3.7-0.6-4.8-1.8c-1.1-1.2-1.7-3-1.7-5.3     c0-2.3,0.6-4,1.7-5.2c1.1-1.2,2.7-1.8,4.9-1.8c2.1,0,3.7,0.6,4.8,1.8C1227.7,131.2,1228.3,132.9,1228.3,135.2z M1218.3,135.2     c0,1.5,0.3,2.7,0.9,3.5c0.6,0.8,1.5,1.2,2.6,1.2c2.3,0,3.5-1.5,3.5-4.6c0-3.1-1.2-4.6-3.5-4.6c-1.2,0-2,0.4-2.6,1.2     C1218.5,132.5,1218.3,133.6,1218.3,135.2z"
              />
              <path
                class="st67"
                d="M1243.3,142.1h-3.7l-6-10.4h-0.1c0.1,1.8,0.2,3.1,0.2,3.9v6.5h-2.6v-13.7h3.7l6,10.3h0.1     c-0.1-1.8-0.1-3-0.1-3.8v-6.5h2.6V142.1z"
              />
              <path
                class="st67"
                d="M1252,142.1h-2.9v-11.3h-3.7v-2.4h10.4v2.4h-3.7V142.1z"
              />
              <path
                class="st67"
                d="M1266.2,142.1l-1-3.3h-5l-1,3.3h-3.1l4.8-13.8h3.6l4.9,13.8H1266.2z M1264.6,136.4c-0.9-3-1.4-4.6-1.6-5     c-0.1-0.4-0.2-0.7-0.2-0.9c-0.2,0.8-0.8,2.8-1.8,5.9H1264.6z"
              />
              <path
                class="st67"
                d="M1276.3,134.4h5.4v7.1c-0.9,0.3-1.7,0.5-2.5,0.6c-0.8,0.1-1.6,0.2-2.4,0.2c-2.1,0-3.7-0.6-4.7-1.8     c-1.1-1.2-1.6-3-1.6-5.2c0-2.2,0.6-3.9,1.9-5.2c1.3-1.2,3-1.9,5.3-1.9c1.4,0,2.8,0.3,4.1,0.8l-1,2.3c-1-0.5-2-0.8-3.1-0.8     c-1.3,0-2.3,0.4-3,1.3s-1.1,2-1.1,3.4c0,1.5,0.3,2.6,0.9,3.4c0.6,0.8,1.5,1.2,2.7,1.2c0.6,0,1.2-0.1,1.8-0.2v-2.9h-2.6V134.4z"
              />
              <path
                class="st67"
                d="M1292.9,142.1h-7.9v-13.7h7.9v2.4h-5v3h4.6v2.4h-4.6v3.5h5V142.1z"
              />
            </g>
            <g>
              <path class="st67" d="M1192.6,165.1v-13.7h2.9v13.7H1192.6z" />
              <path
                class="st67"
                d="M1204.8,165.1l-3.3-10.8h-0.1c0.1,2.2,0.2,3.6,0.2,4.4v6.4h-2.6v-13.7h4l3.2,10.5h0.1l3.4-10.5h4v13.7h-2.7     v-6.5c0-0.3,0-0.7,0-1.1c0-0.4,0.1-1.5,0.1-3.2h-0.1l-3.5,10.7H1204.8z"
              />
              <path
                class="st67"
                d="M1226.5,155.7c0,1.5-0.5,2.6-1.4,3.4s-2.2,1.2-3.9,1.2h-1.2v4.9h-2.9v-13.7h4.4c1.7,0,2.9,0.4,3.8,1.1     C1226.1,153.2,1226.5,154.3,1226.5,155.7z M1220,157.8h1c0.9,0,1.6-0.2,2-0.5c0.4-0.4,0.7-0.9,0.7-1.5c0-0.7-0.2-1.2-0.6-1.5     s-1-0.5-1.8-0.5h-1.3V157.8z"
              />
              <path
                class="st67"
                d="M1232,159.8v5.3h-2.9v-13.7h4c1.9,0,3.2,0.3,4.1,1c0.9,0.7,1.3,1.7,1.3,3.1c0,0.8-0.2,1.5-0.7,2.2     c-0.4,0.6-1.1,1.1-1.9,1.5c2.1,3.1,3.4,5.1,4,6h-3.2l-3.3-5.3H1232z M1232,157.5h0.9c0.9,0,1.6-0.2,2-0.5     c0.4-0.3,0.7-0.8,0.7-1.4c0-0.7-0.2-1.1-0.7-1.4c-0.4-0.3-1.1-0.4-2.1-0.4h-0.9V157.5z"
              />
              <path
                class="st67"
                d="M1254.3,158.2c0,2.3-0.6,4-1.7,5.2c-1.1,1.2-2.7,1.8-4.8,1.8s-3.7-0.6-4.8-1.8c-1.1-1.2-1.7-3-1.7-5.3     c0-2.3,0.6-4,1.7-5.2c1.1-1.2,2.7-1.8,4.9-1.8c2.1,0,3.7,0.6,4.8,1.8C1253.7,154.2,1254.3,156,1254.3,158.2z M1244.2,158.2     c0,1.5,0.3,2.7,0.9,3.5c0.6,0.8,1.5,1.2,2.6,1.2c2.3,0,3.5-1.5,3.5-4.6c0-3.1-1.2-4.6-3.5-4.6c-1.2,0-2,0.4-2.6,1.2     C1244.5,155.5,1244.2,156.7,1244.2,158.2z"
              />
              <path
                class="st67"
                d="M1264.9,151.4h2.9l-4.7,13.7h-3.2l-4.7-13.7h2.9l2.6,8.2c0.1,0.5,0.3,1,0.4,1.7s0.2,1.1,0.3,1.3     c0.1-0.6,0.3-1.6,0.7-3L1264.9,151.4z"
              />
              <path
                class="st67"
                d="M1277.5,165.1h-7.9v-13.7h7.9v2.4h-5v3h4.6v2.4h-4.6v3.5h5V165.1z"
              />
              <path
                class="st67"
                d="M1286.2,165.1l-3.3-10.8h-0.1c0.1,2.2,0.2,3.6,0.2,4.4v6.4h-2.6v-13.7h4l3.2,10.5h0.1l3.4-10.5h4v13.7h-2.7     v-6.5c0-0.3,0-0.7,0-1.1c0-0.4,0.1-1.5,0.1-3.2h-0.1l-3.5,10.7H1286.2z"
              />
              <path
                class="st67"
                d="M1306.4,165.1h-7.9v-13.7h7.9v2.4h-5v3h4.6v2.4h-4.6v3.5h5V165.1z"
              />
              <path
                class="st67"
                d="M1321.4,165.1h-3.7l-6-10.4h-0.1c0.1,1.8,0.2,3.1,0.2,3.9v6.5h-2.6v-13.7h3.7l6,10.3h0.1     c-0.1-1.8-0.1-3-0.1-3.8v-6.5h2.6V165.1z"
              />
              <path
                class="st67"
                d="M1330.1,165.1h-2.9v-11.3h-3.7v-2.4h10.4v2.4h-3.7V165.1z"
              />
              <path
                class="st67"
                d="M1344.1,161.3c0,1.2-0.4,2.2-1.3,2.9s-2.1,1.1-3.7,1.1c-1.5,0-2.8-0.3-3.9-0.8v-2.7     c0.9,0.4,1.7,0.7,2.3,0.9c0.6,0.2,1.2,0.3,1.8,0.3c0.6,0,1.1-0.1,1.5-0.4c0.3-0.2,0.5-0.6,0.5-1.1c0-0.3-0.1-0.5-0.2-0.7     s-0.4-0.4-0.7-0.6s-0.9-0.5-1.8-0.9c-0.8-0.4-1.5-0.8-1.9-1.1s-0.8-0.8-1-1.3c-0.3-0.5-0.4-1-0.4-1.7c0-1.2,0.4-2.2,1.2-2.9     c0.8-0.7,2-1,3.4-1c0.7,0,1.4,0.1,2,0.3s1.3,0.4,2,0.7l-0.9,2.3c-0.7-0.3-1.3-0.5-1.8-0.6c-0.5-0.1-0.9-0.2-1.4-0.2     c-0.5,0-1,0.1-1.3,0.4c-0.3,0.3-0.4,0.6-0.4,1c0,0.3,0.1,0.5,0.2,0.7c0.1,0.2,0.3,0.4,0.6,0.6c0.3,0.2,0.9,0.5,1.8,1     c1.3,0.6,2.2,1.2,2.6,1.8C1343.8,159.7,1344.1,160.4,1344.1,161.3z"
              />
            </g>
          </g>
        </a>
        <g id="frontage-line">
          <line class="st68" x1="1236.3" y1="186.3" x2="1236.3" y2="541.2" />
          <circle class="st68" cx="1236.3" cy="542.7" r="1.4" />
          <ellipse class="st36" cx="1234.9" cy="544.7" rx="34.6" ry="14.8" />
        </g>
        <a
          :href="links.tree"
          @focus="handleFocus('tree')"
          @mouseenter="handleFocus('tree')"
          @mouseleave="focus['tree'] = false"
          :style="focus['tree'] ? focusStyles : undefined"
        >
          <g>
            <rect x="393.7" y="168.7" class="st15" width="310" height="80.1" />
            <path
              class="st67"
              d="M458.6,217.8c-1.2,4.1-11.6,7.2-17.2,7.5v-24.2h-1.9v24.2c-5.6-0.3-16-3.5-17.2-7.5     c-0.6-2.1,4.5-7.9,8.2-11.3l1.3-1.1l-1.6-0.5c-1.6-0.5-3.2-1.3-3.4-1.6c-1-4.5,9-15.1,13.7-19.6c4.7,4.5,14.8,15.1,13.7,19.6     c-0.2,0.4-1.9,1.2-3.5,1.6l-1.6,0.5l1.3,1.1C454.1,209.9,459.2,215.7,458.6,217.8 M452.8,206.1c1.4-0.6,3-1.4,3.2-2.5     c1.5-6.6-13.2-20.5-14.9-22.1l-0.6-0.6l-0.6,0.6c-1.7,1.6-16.5,15.5-14.9,22.1c0.2,1.1,1.8,1.9,3.2,2.5     c-2.8,2.7-8.6,8.8-7.6,12.2c1.6,5.5,13.4,8.6,19,8.9v9.3h1.9v-9.3c5.6-0.3,17.5-3.4,19.1-8.9     C461.4,214.9,455.6,208.9,452.8,206.1"
            />
            <text transform="matrix(1 0 0 1 480 205)" class="st67 st71 st72">
              TREE PRESERVATION
            </text>
            <text transform="matrix(1 0 0 1 480 229)" class="st67 st71 st72">
              & PLANTING
            </text>
          </g>
        </a>
        <g id="trees-line">
          <line class="st68" x1="576.4" y1="248.8" x2="576.4" y2="428.9" />
          <circle class="st68" cx="576.4" cy="430.3" r="1.4" />
          <ellipse class="st37" cx="693.5" cy="551.4" rx="34.6" ry="14.8" />
        </g>
        <a
          aria-label="Skip to access and loading section"
          :href="links.access"
          @focus="handleFocus('access')"
          @mouseenter="handleFocus('access')"
          @mouseleave="focus['access'] = false"
          :style="focus['access'] ? focusStyles : undefined"
        >
          <g>
            <rect x="348" y="661.6" class="st69" width="203.2" height="80.1" />
            <g>
              <path
                class="st67"
                d="M448.3,697.5l-1-3.3h-5l-1,3.3h-3.1l4.8-13.8h3.6l4.9,13.8H448.3z M446.6,691.8c-0.9-3-1.4-4.6-1.6-5       s-0.2-0.7-0.2-0.9c-0.2,0.8-0.8,2.8-1.8,5.9H446.6z"
              />
              <path
                class="st67"
                d="M459,686c-1.1,0-1.9,0.4-2.5,1.2s-0.9,2-0.9,3.4c0,3.1,1.1,4.6,3.4,4.6c1,0,2.1-0.2,3.5-0.7v2.4       c-1.1,0.5-2.4,0.7-3.8,0.7c-2,0-3.5-0.6-4.6-1.8s-1.6-2.9-1.6-5.2c0-1.4,0.3-2.7,0.8-3.7c0.5-1.1,1.3-1.9,2.2-2.5       s2.1-0.9,3.4-0.9s2.7,0.3,4,1l-0.9,2.4c-0.5-0.2-1-0.5-1.5-0.6S459.5,686,459,686z"
              />
              <path
                class="st67"
                d="M471.2,686c-1.1,0-1.9,0.4-2.5,1.2s-0.9,2-0.9,3.4c0,3.1,1.1,4.6,3.4,4.6c1,0,2.1-0.2,3.5-0.7v2.4       c-1.1,0.5-2.4,0.7-3.8,0.7c-2,0-3.5-0.6-4.6-1.8s-1.6-2.9-1.6-5.2c0-1.4,0.3-2.7,0.8-3.7c0.5-1.1,1.3-1.9,2.2-2.5       s2.1-0.9,3.4-0.9s2.7,0.3,4,1l-0.9,2.4c-0.5-0.2-1-0.5-1.5-0.6S471.7,686,471.2,686z"
              />
              <path
                class="st67"
                d="M485.5,697.5h-7.9v-13.7h7.9v2.4h-5v3h4.6v2.4h-4.6v3.5h5V697.5z"
              />
              <path
                class="st67"
                d="M496.5,693.7c0,1.2-0.4,2.2-1.3,2.9s-2.1,1.1-3.7,1.1c-1.5,0-2.8-0.3-3.9-0.8v-2.7       c0.9,0.4,1.7,0.7,2.4,0.9c0.6,0.2,1.2,0.3,1.8,0.3c0.6,0,1.1-0.1,1.5-0.4s0.5-0.6,0.5-1.1c0-0.3-0.1-0.5-0.2-0.7       s-0.4-0.4-0.7-0.6c-0.3-0.2-0.9-0.5-1.8-0.9c-0.8-0.4-1.5-0.8-1.9-1.1s-0.8-0.8-1-1.3s-0.4-1-0.4-1.7c0-1.2,0.4-2.2,1.2-2.9       s2-1,3.4-1c0.7,0,1.4,0.1,2,0.3s1.3,0.4,2,0.7l-0.9,2.3c-0.7-0.3-1.3-0.5-1.8-0.6s-0.9-0.2-1.4-0.2c-0.6,0-1,0.1-1.3,0.4       s-0.4,0.6-0.4,1c0,0.3,0.1,0.5,0.2,0.7s0.3,0.4,0.6,0.6c0.3,0.2,0.9,0.5,1.8,1c1.3,0.6,2.2,1.2,2.6,1.8       C496.2,692,496.5,692.8,496.5,693.7z"
              />
              <path
                class="st67"
                d="M507.1,693.7c0,1.2-0.4,2.2-1.3,2.9s-2.1,1.1-3.7,1.1c-1.5,0-2.8-0.3-3.9-0.8v-2.7       c0.9,0.4,1.7,0.7,2.4,0.9c0.6,0.2,1.2,0.3,1.8,0.3c0.6,0,1.1-0.1,1.5-0.4s0.5-0.6,0.5-1.1c0-0.3-0.1-0.5-0.2-0.7       s-0.4-0.4-0.7-0.6c-0.3-0.2-0.9-0.5-1.8-0.9c-0.8-0.4-1.5-0.8-1.9-1.1s-0.8-0.8-1-1.3s-0.4-1-0.4-1.7c0-1.2,0.4-2.2,1.2-2.9       s2-1,3.4-1c0.7,0,1.4,0.1,2,0.3s1.3,0.4,2,0.7l-0.9,2.3c-0.7-0.3-1.3-0.5-1.8-0.6s-0.9-0.2-1.4-0.2c-0.6,0-1,0.1-1.3,0.4       s-0.4,0.6-0.4,1c0,0.3,0.1,0.5,0.2,0.7s0.3,0.4,0.6,0.6c0.3,0.2,0.9,0.5,1.8,1c1.3,0.6,2.2,1.2,2.6,1.8       C506.8,692,507.1,692.8,507.1,693.7z"
              />
              <path
                class="st67"
                d="M527.2,697.5h-3.5l-1.1-1.1c-1.2,0.8-2.5,1.2-4.1,1.2c-1.5,0-2.7-0.3-3.6-1.1c-0.9-0.7-1.3-1.6-1.3-2.8       c0-0.9,0.2-1.6,0.6-2.2s1-1.2,1.9-1.7c-0.5-0.5-0.8-1.1-1-1.5c-0.2-0.5-0.3-1-0.3-1.6c0-1,0.4-1.7,1.1-2.3s1.7-0.9,2.9-0.9       s2.1,0.3,2.8,0.8s1,1.3,1,2.2c0,0.7-0.2,1.4-0.6,2s-1.1,1.2-2.1,1.8l2.7,2.6c0.4-0.7,0.8-1.7,1.2-2.8h3       c-0.2,0.8-0.5,1.7-0.9,2.5s-0.8,1.5-1.3,2.1L527.2,697.5z M516.6,693.5c0,0.5,0.2,1,0.6,1.3s0.9,0.5,1.6,0.5       c0.8,0,1.5-0.2,2.1-0.6l-3.1-3.1c-0.4,0.3-0.6,0.6-0.9,0.9C516.7,692.7,516.6,693.1,516.6,693.5z M520,686.8       c0-0.3-0.1-0.6-0.3-0.8s-0.5-0.3-0.9-0.3s-0.7,0.1-1,0.3s-0.4,0.5-0.4,0.9c0,0.6,0.3,1.2,0.9,1.8c0.5-0.3,1-0.6,1.2-0.9       C519.7,687.5,520,687.2,520,686.8z"
              />
            </g>
            <g>
              <path
                class="st67"
                d="M439.9,720.5v-13.7h2.9v11.3h5.6v2.4H439.9z"
              />
              <path
                class="st67"
                d="M463.2,713.6c0,2.3-0.6,4-1.7,5.2s-2.7,1.8-4.8,1.8c-2.1,0-3.7-0.6-4.8-1.8c-1.1-1.2-1.7-3-1.7-5.3       s0.6-4,1.7-5.2s2.7-1.8,4.9-1.8c2.1,0,3.7,0.6,4.8,1.8C462.6,709.6,463.2,711.4,463.2,713.6z M453.2,713.6       c0,1.5,0.3,2.7,0.9,3.5s1.5,1.2,2.6,1.2c2.3,0,3.5-1.5,3.5-4.6s-1.2-4.6-3.5-4.6c-1.2,0-2,0.4-2.6,1.2       C453.5,711,453.2,712.1,453.2,713.6z"
              />
              <path
                class="st67"
                d="M474.4,720.5l-1-3.3h-5l-1,3.3h-3.1l4.8-13.8h3.6l4.9,13.8H474.4z M472.7,714.8c-0.9-3-1.4-4.6-1.6-5       s-0.2-0.7-0.2-0.9c-0.2,0.8-0.8,2.8-1.8,5.9H472.7z"
              />
              <path
                class="st67"
                d="M490.7,713.5c0,2.3-0.6,4-1.9,5.2s-3.1,1.8-5.6,1.8h-3.9v-13.7h4.3c2.2,0,4,0.6,5.2,1.8       S490.7,711.4,490.7,713.5z M487.6,713.6c0-2.9-1.3-4.4-3.9-4.4h-1.5v8.9h1.2C486.2,718.1,487.6,716.6,487.6,713.6z"
              />
              <path class="st67" d="M493.5,720.5v-13.7h2.9v13.7H493.5z" />
              <path
                class="st67"
                d="M512,720.5h-3.7l-6-10.4h-0.1c0.1,1.8,0.2,3.1,0.2,3.9v6.5h-2.6v-13.7h3.7l6,10.3h0.1       c-0.1-1.8-0.1-3-0.1-3.8v-6.5h2.6L512,720.5L512,720.5z"
              />
              <path
                class="st67"
                d="M520.7,712.8h5.4v7.1c-0.9,0.3-1.7,0.5-2.5,0.6s-1.6,0.2-2.4,0.2c-2.1,0-3.7-0.6-4.7-1.8       c-1.1-1.2-1.6-3-1.6-5.2s0.6-3.9,1.9-5.2s3-1.9,5.3-1.9c1.4,0,2.8,0.3,4.1,0.8l-1,2.3c-1-0.5-2-0.8-3.1-0.8       c-1.3,0-2.3,0.4-3,1.3s-1.1,2-1.1,3.4c0,1.5,0.3,2.6,0.9,3.4c0.6,0.8,1.5,1.2,2.7,1.2c0.6,0,1.2-0.1,1.8-0.2v-2.8h-2.6       L520.7,712.8L520.7,712.8z"
              />
            </g>
            <g>
              <path
                class="st67"
                d="M373,674.7c-6.1,0-11.1,5-11.1,11.1c0,2.6,0.9,5.1,2.6,7.1l8.1,9.6c0.4,0.4,1.1,0.4,1.4,0l8.1-9.6       c1.7-2,2.6-4.5,2.6-7.1c0-6.1-5-11.1-11.1-11.1H373z M373.3,704.6c-0.8,0-1.5-0.3-2-0.9l-8.1-9.6c-1.9-2.3-3-5.2-3-8.2       c0-7,5.7-12.8,12.8-12.8h0.6c7,0,12.8,5.7,12.8,12.8c0,3-1.1,5.9-3,8.2l-8.1,9.6C374.8,704.2,374,704.6,373.3,704.6"
              />
              <path
                class="st67"
                d="M373.3,679.7c-3.4,0-6.1,2.8-6.1,6.1s2.8,6.1,6.1,6.1s6.1-2.8,6.1-6.1S376.7,679.7,373.3,679.7        M373.3,693.6c-4.3,0-7.8-3.5-7.8-7.8s3.5-7.8,7.8-7.8s7.8,3.5,7.8,7.8C381.1,690.1,377.6,693.6,373.3,693.6"
              />
              <path
                class="st67"
                d="M411.6,721h-24.9c-2.7,0-4.9-2.2-4.9-4.9v-0.3c0-2.7,2.2-4.9,4.9-4.9h1.2c1.8,0,3.2-1.4,3.2-3.2       s-1.4-3.2-3.2-3.2h-14.6c-0.5,0-0.8-0.4-0.8-0.8c0-0.5,0.4-0.8,0.8-0.8h14.6c2.7,0,4.9,2.2,4.9,4.9s-2.2,4.9-4.9,4.9h-1.2       c-1.8,0-3.2,1.4-3.2,3.2v0.3c0,1.8,1.4,3.2,3.2,3.2h24.9c0.5,0,0.8,0.4,0.8,0.8S412,721,411.6,721"
              />
              <path
                class="st67"
                d="M420.4,725.2h2l-3.4-5.1l3.3-4.8h-1.9L418,719l-2.3-3.7h-2l3.3,4.9l-3.4,5.1h1.9l2.5-3.9L420.4,725.2z        M424.8,726.4h-5.1l-1.8-2.8l-1.8,2.8H411l4.3-6.3l-4.1-6.2h5.1l1.6,2.6l1.6-2.6h5.1l-4.1,6.1L424.8,726.4z"
              />
            </g>
          </g>
        </a>
        <g id="access-line">
          <polyline
            class="st68"
            points="461.8,661.9 461.8,549.3 687.8,549.3    "
          />
          <circle class="st68" cx="689.2" cy="549.3" r="1.4" />
        </g>
        <a
          :href="links.stormwater"
          @focus="handleFocus('stormwater')"
          @mouseenter="handleFocus('stormwater')"
          @mouseleave="focus['stormwater'] = false"
          :style="focus['stormwater'] ? focusStyles : undefined"
        >
          <g>
            <rect
              x="927"
              y="824.2"
              class="fill-orange-500"
              width="261"
              height="80.1"
            />
            <g>
              <path
                class="st67"
                d="M1027.1,856.3c0,1.2-0.4,2.2-1.3,2.9s-2.1,1.1-3.7,1.1c-1.5,0-2.8-0.3-3.9-0.8v-2.7       c0.9,0.4,1.7,0.7,2.3,0.9s1.2,0.3,1.8,0.3s1.1-0.1,1.5-0.4c0.4-0.3,0.5-0.6,0.5-1.1c0-0.3-0.1-0.5-0.2-0.7s-0.4-0.4-0.7-0.6       c-0.3-0.2-0.9-0.5-1.8-0.9c-0.8-0.4-1.5-0.8-1.9-1.1s-0.8-0.8-1-1.3c-0.3-0.5-0.4-1-0.4-1.7c0-1.2,0.4-2.2,1.2-2.9s2-1,3.4-1       c0.7,0,1.4,0.1,2,0.3s1.3,0.4,2,0.7l-0.9,2.3c-0.7-0.3-1.3-0.5-1.8-0.6s-0.9-0.2-1.4-0.2s-1,0.1-1.3,0.4       c-0.3,0.3-0.4,0.6-0.4,1c0,0.3,0.1,0.5,0.2,0.7s0.3,0.4,0.6,0.6c0.3,0.2,0.9,0.5,1.8,1c1.3,0.6,2.2,1.2,2.6,1.8       C1026.8,854.6,1027.1,855.4,1027.1,856.3z"
              />
              <path
                class="st67"
                d="M1034.8,860.1h-2.9v-11.3h-3.7v-2.4h10.4v2.4h-3.7v11.3H1034.8z"
              />
              <path
                class="st67"
                d="M1053.1,853.2c0,2.3-0.6,4-1.7,5.2s-2.7,1.8-4.8,1.8s-3.7-0.6-4.8-1.8c-1.1-1.2-1.7-3-1.7-5.3       s0.6-4,1.7-5.2s2.7-1.8,4.9-1.8c2.1,0,3.7,0.6,4.8,1.8C1052.6,849.2,1053.1,850.9,1053.1,853.2z M1043.1,853.2       c0,1.5,0.3,2.7,0.9,3.5s1.5,1.2,2.6,1.2c2.3,0,3.5-1.5,3.5-4.6s-1.2-4.6-3.5-4.6c-1.2,0-2,0.4-2.6,1.2       S1043.1,851.7,1043.1,853.2z"
              />
              <path
                class="st67"
                d="M1058.9,854.8v5.3h-2.9v-13.7h4c1.9,0,3.2,0.3,4.1,1s1.3,1.7,1.3,3.1c0,0.8-0.2,1.5-0.7,2.2       c-0.4,0.6-1.1,1.1-1.9,1.5c2.1,3.1,3.4,5.1,4,6h-3.2l-3.3-5.3L1058.9,854.8L1058.9,854.8z M1058.9,852.4h0.9       c0.9,0,1.6-0.2,2-0.5s0.7-0.8,0.7-1.4c0-0.7-0.2-1.1-0.7-1.4s-1.1-0.4-2.1-0.4h-0.9L1058.9,852.4L1058.9,852.4z"
              />
              <path
                class="st67"
                d="M1074.5,860.1l-3.3-10.8h-0.1c0.1,2.2,0.2,3.6,0.2,4.4v6.4h-2.6v-13.7h4l3.2,10.5h0.1l3.4-10.5h4v13.7       h-2.7v-6.5c0-0.3,0-0.7,0-1.1c0-0.4,0.1-1.5,0.1-3.2h-0.1l-3.5,10.7L1074.5,860.1L1074.5,860.1z"
              />
              <path
                class="st67"
                d="M1100.1,860.1h-3.3l-1.9-7.2c-0.1-0.3-0.2-0.8-0.4-1.6c-0.2-0.8-0.3-1.3-0.3-1.6c0,0.3-0.1,0.9-0.3,1.6       c-0.2,0.7-0.3,1.3-0.3,1.6l-1.8,7.2h-3.3l-3.5-13.7h2.9l1.8,7.5c0.3,1.4,0.5,2.6,0.7,3.6c0-0.4,0.1-0.9,0.3-1.7       c0.1-0.7,0.3-1.3,0.4-1.7l2-7.7h2.7l2,7.7c0.1,0.3,0.2,0.9,0.3,1.6c0.1,0.7,0.2,1.3,0.3,1.8c0.1-0.5,0.2-1.1,0.3-1.8       s0.3-1.3,0.4-1.8l1.7-7.5h2.9L1100.1,860.1z"
              />
              <path
                class="st67"
                d="M1113.7,860.1l-1-3.3h-5l-1,3.3h-3.1l4.8-13.8h3.6l4.9,13.8H1113.7z M1112,854.4c-0.9-3-1.4-4.6-1.6-5       c-0.1-0.4-0.2-0.7-0.2-0.9c-0.2,0.8-0.8,2.8-1.8,5.9H1112z"
              />
              <path
                class="st67"
                d="M1123.9,860.1h-2.9v-11.3h-3.7v-2.4h10.4v2.4h-3.7v11.3H1123.9z"
              />
              <path
                class="st67"
                d="M1137.6,860.1h-7.9v-13.7h7.9v2.4h-5v3h4.6v2.4h-4.6v3.5h5V860.1z"
              />
              <path
                class="st67"
                d="M1143.4,854.8v5.3h-2.9v-13.7h4c1.9,0,3.2,0.3,4.1,1s1.3,1.7,1.3,3.1c0,0.8-0.2,1.5-0.7,2.2       c-0.4,0.6-1.1,1.1-1.9,1.5c2.1,3.1,3.4,5.1,4,6h-3.2l-3.3-5.3L1143.4,854.8L1143.4,854.8z M1143.4,852.4h0.9       c0.9,0,1.6-0.2,2-0.5s0.7-0.8,0.7-1.4c0-0.7-0.2-1.1-0.7-1.4s-1.1-0.4-2.1-0.4h-0.9L1143.4,852.4L1143.4,852.4z"
              />
            </g>
            <g>
              <path
                class="st67"
                d="M1024.8,883.1l-3.3-10.8h-0.1c0.1,2.2,0.2,3.6,0.2,4.4v6.4h-2.6v-13.7h4l3.2,10.5h0.1l3.4-10.5h4v13.7       h-2.7v-6.5c0-0.3,0-0.7,0-1.1c0-0.4,0.1-1.5,0.1-3.2h-0.1l-3.5,10.7L1024.8,883.1L1024.8,883.1z"
              />
              <path
                class="st67"
                d="M1045.5,883.1l-1-3.3h-5l-1,3.3h-3.1l4.8-13.8h3.6l4.9,13.8H1045.5z M1043.8,877.4c-0.9-3-1.4-4.6-1.6-5       c-0.1-0.4-0.2-0.7-0.2-0.9c-0.2,0.8-0.8,2.8-1.8,5.9H1043.8z"
              />
              <path
                class="st67"
                d="M1062.5,883.1h-3.7l-6-10.4h-0.1c0.1,1.8,0.2,3.1,0.2,3.9v6.5h-2.6v-13.7h3.7l6,10.3h0.1       c-0.1-1.8-0.1-3-0.1-3.8v-6.5h2.6L1062.5,883.1L1062.5,883.1z"
              />
              <path
                class="st67"
                d="M1074.4,883.1l-1-3.3h-5l-1,3.3h-3.1l4.8-13.8h3.6l4.9,13.8H1074.4z M1072.7,877.4c-0.9-3-1.4-4.6-1.6-5       c-0.1-0.4-0.2-0.7-0.2-0.9c-0.2,0.8-0.8,2.8-1.8,5.9H1072.7z"
              />
              <path
                class="st67"
                d="M1084.4,875.4h5.4v7.1c-0.9,0.3-1.7,0.5-2.5,0.6s-1.6,0.2-2.4,0.2c-2.1,0-3.7-0.6-4.7-1.8       c-1.1-1.2-1.6-3-1.6-5.2s0.6-3.9,1.9-5.2c1.3-1.2,3-1.9,5.3-1.9c1.4,0,2.8,0.3,4.1,0.8l-1,2.3c-1-0.5-2-0.8-3.1-0.8       c-1.3,0-2.3,0.4-3,1.3s-1.1,2-1.1,3.4c0,1.5,0.3,2.6,0.9,3.4c0.6,0.8,1.5,1.2,2.7,1.2c0.6,0,1.2-0.1,1.8-0.2v-2.9h-2.6v-2.3       L1084.4,875.4L1084.4,875.4z"
              />
              <path
                class="st67"
                d="M1101,883.1h-7.9v-13.7h7.9v2.4h-5v3h4.6v2.4h-4.6v3.5h5V883.1z"
              />
              <path
                class="st67"
                d="M1109.7,883.1l-3.3-10.8h-0.1c0.1,2.2,0.2,3.6,0.2,4.4v6.4h-2.6v-13.7h4l3.2,10.5h0.1l3.4-10.5h4v13.7       h-2.7v-6.5c0-0.3,0-0.7,0-1.1c0-0.4,0.1-1.5,0.1-3.2h-0.1l-3.5,10.7L1109.7,883.1L1109.7,883.1z"
              />
              <path
                class="st67"
                d="M1129.9,883.1h-7.9v-13.7h7.9v2.4h-5v3h4.6v2.4h-4.6v3.5h5V883.1z"
              />
              <path
                class="st67"
                d="M1144.9,883.1h-3.7l-6-10.4h-0.1c0.1,1.8,0.2,3.1,0.2,3.9v6.5h-2.6v-13.7h3.7l6,10.3h0.1       c-0.1-1.8-0.1-3-0.1-3.8v-6.5h2.6L1144.9,883.1L1144.9,883.1z"
              />
              <path
                class="st67"
                d="M1153.7,883.1h-2.9v-11.3h-3.7v-2.4h10.4v2.4h-3.7v11.3H1153.7z"
              />
            </g>
            <g>
              <path
                class="st67"
                d="M977.8,852.6l6.4-12.1l6.4,12.1c1.9,3.5,0.5,7.9-3,9.7c-3.6,1.9-7.9,0.5-9.8-3       C976.6,857.2,976.6,854.7,977.8,852.6L977.8,852.6z M984.2,865c5.1,0,9.2-4.1,9.2-9.1c0-1.5-0.4-2.9-1.1-4.2L985,838       c-0.2-0.5-0.8-0.6-1.3-0.4c-0.2,0.1-0.3,0.2-0.4,0.4l-7.3,13.7c-2.4,4.4-0.6,9.9,3.8,12.3C981.2,864.6,982.7,865,984.2,865"
              />
              <path
                class="st67"
                d="M955.9,841.5l3.2-6.1l3.2,6.1c0.9,1.8,0.3,3.9-1.5,4.9c-1.4,0.8-3.2,0.5-4.3-0.6       C955.4,844.7,955.1,842.9,955.9,841.5L955.9,841.5z M955.2,847.1c2.2,2.1,5.7,2.1,7.8,0c1.7-1.7,2.1-4.3,1-6.4l-4-7.6       c-0.2-0.5-0.8-0.6-1.3-0.4c-0.2,0.1-0.3,0.2-0.4,0.4l-4.1,7.6C953,842.8,953.4,845.4,955.2,847.1"
              />
              <path
                class="st67"
                d="M996.3,885.3c0.7,0.9,1.6,1.7,2.5,2.4c0.5,0.3,1,0.1,1.3-0.3c0.2-0.4,0.1-0.9-0.3-1.2       c-0.8-0.6-1.5-1.3-2.1-2.1c-1.4-1.6-3-3.5-6.2-3.5s-4.8,1.8-6.2,3.5c-1.3,1.5-2.5,2.8-4.8,2.8s-3.5-1.3-4.8-2.8       c-1.4-1.6-3-3.5-6.2-3.5s-4.8,1.8-6.2,3.5c-1.3,1.5-2.5,2.8-4.8,2.8s-3.5-1.3-4.8-2.8c-0.9-1.1-2-2.1-3.3-2.8       c-0.5-0.2-1,0-1.3,0.4c-0.2,0.5,0,1,0.4,1.3c1,0.6,1.9,1.4,2.7,2.4c1.4,1.6,3,3.5,6.2,3.5s4.8-1.9,6.2-3.5       c1.3-1.5,2.5-2.8,4.8-2.8s3.5,1.3,4.8,2.8c1.4,1.6,3,3.5,6.2,3.5s4.8-1.8,6.2-3.5c1.3-1.5,2.5-2.8,4.8-2.8       C993.8,882.5,995,883.8,996.3,885.3"
              />
              <path
                class="st67"
                d="M950.5,870.7c-0.5-0.2-1,0-1.3,0.4c-0.2,0.5,0,1,0.4,1.3c1,0.6,1.9,1.4,2.7,2.4c1.4,1.6,3,3.5,6.2,3.5       s4.8-1.9,6.2-3.5c1.3-1.5,2.5-2.8,4.8-2.8s3.5,1.3,4.8,2.8c1.4,1.6,3,3.5,6.2,3.5s4.8-1.8,6.2-3.5c1.3-1.5,2.5-2.8,4.8-2.8       s3.5,1.3,4.8,2.8c0.7,0.9,1.6,1.7,2.5,2.4c0.5,0.3,1,0.1,1.3-0.3c0.2-0.4,0.1-0.9-0.3-1.2c-0.8-0.6-1.5-1.3-2.1-2.1       c-1.4-1.6-3-3.5-6.2-3.5s-4.8,1.8-6.2,3.4c-1.3,1.5-2.5,2.8-4.8,2.8s-3.5-1.3-4.8-2.8c-1.4-1.6-3-3.5-6.2-3.5s-4.8,1.8-6.2,3.5       c-1.3,1.5-2.5,2.8-4.8,2.8s-3.5-1.3-4.8-2.8C952.9,872.3,951.8,871.4,950.5,870.7"
              />
            </g>
          </g>
        </a>
        <g id="stormwater-line">
          <polyline
            class="st68"
            points="926.6,864.2 791.5,864.2 791.5,728.2    "
          />
          <circle class="st68" cx="791.5" cy="726.7" r="1.4" />
        </g>
        <a
          :href="links.utility"
          @focus="handleFocus('utility')"
          @mouseenter="handleFocus('utility')"
          @mouseleave="focus['utility'] = false"
          :style="focus['utility'] ? focusStyles : undefined"
        >
          <g>
            <rect
              x="1364.4"
              y="664.5"
              class="fill-blue-500"
              width="227.8"
              height="80.1"
            />
            <g>
              <path
                class="st67"
                d="M1432.1,725.8c-0.5,0.9-1.4,1.4-2.4,1.4h-43.2c-1,0-1.9-0.5-2.4-1.4s-0.6-1.9-0.1-2.8l21.6-43.1       c0.5-1,1.5-1.6,2.6-1.6l0,0c1.1,0,2.1,0.6,2.6,1.6l21.6,43.1C1432.7,724,1432.6,725,1432.1,725.8 M1433.7,722.3l-21.6-43.1       c-0.8-1.5-2.3-2.5-4-2.5l0,0c-1.7,0-3.3,1-4,2.5l-21.6,43.1c-0.7,1.4-0.6,3,0.2,4.4c0.8,1.3,2.3,2.1,3.8,2.1h43.2       c1.6,0,3-0.8,3.8-2.1C1434.2,725.3,1434.4,723.8,1433.7,722.3"
              />
              <path
                class="st67"
                d="M1401.5,707l8.7-8l-2.7,6.6c-0.3,0.6,0,1.3,0.7,1.6l4.6,1.9c0.2,0.1,0.2,0.2,0.2,0.3s0,0.2-0.1,0.4       l-8.7,8l2.7-6.6c0.3-0.6,0-1.3-0.7-1.6l-4.6-1.9c-0.2-0.1-0.2-0.2-0.2-0.3C1401.2,707.2,1401.2,707,1401.5,707 M1399.7,707.7       c0.1,0.7,0.6,1.3,1.2,1.5l4.3,1.8l-3.3,7.9c-0.2,0.5,0,1.1,0.4,1.5c0.2,0.2,0.5,0.2,0.7,0.2c0.3,0,0.6-0.1,0.8-0.3l10.1-9.2       c0.5-0.5,0.8-1.2,0.6-1.9c-0.1-0.7-0.6-1.3-1.2-1.5l-4.3-1.8l3.3-7.9c0.2-0.5,0-1.1-0.4-1.5c-0.5-0.3-1.1-0.3-1.5,0.1       l-10.1,9.2C1399.8,706.2,1399.6,707,1399.7,707.7"
              />
              <path
                class="st67"
                d="M1387.8,723.5l20.2-40.4l20.3,40.4H1387.8z M1409.1,681.7c-0.2-0.4-0.6-0.7-1.1-0.7l0,0       c-0.5,0-0.9,0.3-1.1,0.7l-20.9,41.7c-0.2,0.4-0.2,0.8,0.1,1.2c0.2,0.4,0.6,0.6,1,0.6h41.9c0.4,0,0.8-0.2,1-0.6       c0.2-0.4,0.2-0.8,0.1-1.2L1409.1,681.7z"
              />
            </g>
            <g>
              <path
                class="st67"
                d="M1467.5,686.7v8.9c0,1-0.2,1.9-0.7,2.7s-1.1,1.3-2,1.8c-0.9,0.4-1.9,0.6-3,0.6c-1.8,0-3.1-0.5-4.1-1.4       s-1.5-2.1-1.5-3.7v-8.8h2.9v8.4c0,1.1,0.2,1.8,0.6,2.3s1.1,0.7,2.1,0.7s1.6-0.2,2.1-0.7c0.4-0.5,0.6-1.3,0.6-2.3v-8.4       L1467.5,686.7L1467.5,686.7z"
              />
              <path
                class="st67"
                d="M1476.2,700.3h-2.9V689h-3.7v-2.4h10.4v2.4h-3.7v11.3H1476.2z"
              />
              <path class="st67" d="M1482,700.3v-13.7h2.9v13.7H1482z" />
              <path
                class="st67"
                d="M1488.4,700.3v-13.7h2.9v11.3h5.6v2.4H1488.4z"
              />
              <path class="st67" d="M1499.2,700.3v-13.7h2.9v13.7H1499.2z" />
              <path
                class="st67"
                d="M1510.9,700.3h-2.9V689h-3.7v-2.4h10.4v2.4h-3.7v11.3H1510.9z"
              />
              <path
                class="st67"
                d="M1521,692.2l2.9-5.6h3.1l-4.5,8.4v5.3h-2.9v-5.2l-4.5-8.5h3.2L1521,692.2z"
              />
            </g>

            <text
              transform="matrix(1 0 0 1 1453.7065 725.1935)"
              class="st67 st71 st72"
            >
              SERVICES
            </text>
          </g>
        </a>
        <g id="utility-line">
          <polyline
            class="st68"
            points="1364.4,712.2 1226.6,712.2 1226.6,680    "
          />
          <circle class="st68" cx="1226.6" cy="678.5" r="1.4" />
        </g>
      </g>
    </g>
  </svg>
</template>
<style>
.st0 {
  @apply stroke-blue-500;
  fill: none;
  stroke-width: 8.2064;
  stroke-miterlimit: 10;
}
.st1 {
  fill: #62848b;
}
.st2 {
  fill: #e0efe6;
}
.st3 {
  fill: #b3b3b3;
}
.st4 {
  @apply stroke-orange-500;
  fill: none;
  stroke-width: 4;
  stroke-miterlimit: 10;
}
.st5 {
  @apply stroke-orange-500;
  fill: none;
  stroke-width: 16;
  stroke-miterlimit: 10;
}
.st6 {
  @apply fill-orange-500;
  fill: #98cbcc;
}
.st7 {
  fill: #808184;
}
.st8 {
  fill: #d0d2d3;
}
.st9 {
  opacity: 5e-2;
}
.st10 {
  fill: #333333;
}
.st11 {
  fill: #a07b53;
}
.st12 {
  fill: #7f5f42;
}
.st13 {
  fill: #53765f;
}
.st14 {
  fill: #a2ceb5;
}
.st15 {
  fill: #64ad84;
}
.st16 {
  fill: #707171;
}
.st17 {
  fill: #cbcbca;
}
.st18 {
  fill: #e5e5e4;
}
.st19 {
  fill: #b3b2b3;
}
.st20 {
  fill: #979797;
}
.st21 {
  fill: #4d4e4e;
}
.st22 {
  fill: #676767;
}
.st23 {
  fill: #d1c6bc;
}
.st24 {
  fill: #c3996b;
}
.st25 {
  fill: #8a5d3b;
}
.st26 {
  fill: none;
  stroke: #676767;
  stroke-miterlimit: 10;
}
.st27 {
  fill: #c1b49a;
  stroke: #929497;
  stroke-width: 2;
  stroke-miterlimit: 10;
}
.st28 {
  clip-path: url(#SVGID_00000082351696130047991720000013438086016062510998_);
}
.st29 {
  fill-rule: evenodd;
  clip-rule: evenodd;
  fill: #64ad84;
}
.st30 {
  fill-rule: evenodd;
  clip-rule: evenodd;
  fill: #a2ceb5;
}
.st31 {
  clip-path: url(#SVGID_00000039827813483328703710000007129120847514170812_);
}
.st32 {
  clip-path: url(#SVGID_00000041980257944332372760000015258378771597582764_);
}
.st33 {
  clip-path: url(#SVGID_00000075865933064250900050000006401483220173351599_);
}
.st34 {
  @apply stroke-blue-500;
  fill: none;
  stroke-width: 16;
  stroke-miterlimit: 10;
}
.st35 {
  @apply fill-blue-500;
}
.st36 {
  opacity: 0.4;
  fill: #00a0af;
  enable-background: new;
}
.st37 {
  @apply fill-purple-500;
  opacity: 0.4;
  enable-background: new;
}
.st40 {
  fill: #61848c;
}
.st41 {
  fill: #dde9df;
}
.st42 {
  fill: none;
  stroke: #99d9df;
  stroke-width: 6.16;
  stroke-miterlimit: 10;
}
.st43 {
  fill: #808285;
}
.st44 {
  fill: #d1d3d4;
}
.st45 {
  fill: #a27c54;
}
.st46 {
  fill: #806042;
}
.st47 {
  fill: #54765e;
}
.st48 {
  fill: #a1c8ae;
}
.st49 {
  fill: #717171;
}
.st50 {
  fill: #cccccc;
}
.st51 {
  fill: #e6e6e5;
}
.st52 {
  fill: #b4b4b4;
}
.st53 {
  fill: #989898;
}
.st54 {
  fill: #4e4e4e;
}
.st55 {
  fill: #d2c7be;
}
.st56 {
  fill: none;
  stroke: #98cbcc;
  stroke-width: 24.6401;
  stroke-miterlimit: 10;
}
.st57 {
  fill: #c49a6c;
}
.st58 {
  fill: #8b5e3c;
}
.st59 {
  fill: none;
  stroke: #676767;
  stroke-width: 1.54;
  stroke-miterlimit: 10;
}
.st60 {
  opacity: 5e-2;
  fill: #333333;
  enable-background: new;
}
.st61 {
  enable-background: new;
}
.st62 {
  @apply fill-blue-400;
}
.st63 {
  @apply fill-blue-300;
}
.st64 {
  fill: #962132;
}
.st65 {
  fill: #b52a3b;
}
.st66 {
  fill: #00a0ae;
}
.st67 {
  fill: #ffffff;
}
.st68 {
  fill: none;
  stroke: #000000;
  stroke-width: 0.7116;
}
.st69 {
  @apply fill-purple-500;
}
.st70 {
  fill: none;
}
.st71 {
  @apply font-sans;
}
.st72 {
  @apply text-xl font-bold;
}
</style>
